import axios, { type AxiosResponse } from 'axios'

import {
  DEFAULT_TOURNAMENT_ENTRY_FEE_GEMS,
  TOURNAMENT_PLACEMENT_CLAIM_MAX_RANK,
  TOURNAMENT_UPCOMING_CARD_COUNT,
} from '../constants/pvpTournament'
import store, { type AppDispatch } from '../store'
import { GET_RESOURCES_SUCCESS } from '../store/user/action-types'
import { getAuthToken, clearAuthToken } from '../utils/authToken'
import {
  alignSessionBeforeRequest,
  refreshAccessToken,
  resetSessionRefreshDedupe,
  invalidateBootstrapRefresh,
  recoverSessionAfter401,
} from '../utils/axiosSessionBootstrap'
import { bindAuthenticatedWalletToAxiosRequest } from '../utils/bindAuthenticatedWalletToAxiosRequest'
import { getOrCreateClientInstallId } from '../utils/clientInstallId'
import { getHardwareMacForRequest } from '../utils/nativeHardwareMac'
import { filterEphemeralPvpRosterCharacters } from '../utils/rosterCharacters'
import { recordServerTimeFromResponseDateHeader } from '../utils/serverClock'
import {
  triggerSessionExpired,
  setSessionExpiresAt,
  clearPendingReauthAfterSessionExpiry,
} from '../utils/sessionExpiration'

import { global } from './global'
import { restCancelMvMAttackByAttacker } from './mvmPendingRest'
import { dedupeInFlight } from './requestDedupe'
import { setNickname, setRememberCode } from './state/game/reducer'
import type {
  PvpActiveRankedBattleCharacterThumb,
  PvpActiveRankedBattleEntry,
  PvpBattleFinishedLogEntry,
  PvpFriendRelationStatus,
  PvpMmrLeaderboardEntry,
  PvpMmrLeaderboardListRow,
  PvpRankedBattleFeedEntry,
  PvpTodayRankedChampion,
  TournamentHubActivityItem,
} from './state/pvp/state'

/**
 * Where the player was when a 500 threw them onto the error page, so Retry can send them back.
 * Read by `ServerErrorPage`; `sessionStorage` so it dies with the tab rather than lingering.
 */
export const ERROR_RETURN_TO_KEY = 'eclipsera_error_return_to'

// axios defaults: see `utils/axiosSessionBootstrap.ts` (also loaded by `utils/callApi.js`)

/**
 * Only persist client session expiry from real auth/session responses.
 * A generic 200 with a numeric `expiresAt` (e.g. quest/building) used to overwrite localStorage and
 * make `isSessionExpiredByTimestamp()` fire immediately — same symptom as "session terminated" after refresh.
 */
function shouldPersistSessionExpiresAtFromBody(response: AxiosResponse): boolean {
  const data = response.data as Record<string, unknown> | undefined
  const expiresAt = data?.expiresAt
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt) || expiresAt <= 0) {
    return false
  }
  const nowSec = Math.floor(Date.now() / 1000)
  if (expiresAt < nowSec - 3600 || expiresAt > nowSec + 10 * 86400) {
    return false
  }
  return data?.refreshed === true || data?.sessionEstablished === true
}

/** After a successful `/.../claim` response, refetch `GET /mission` once, 2s after the latest claim in a burst. */
const MISSION_REFETCH_AFTER_CLAIM_MS = 2000
let missionRefetchAfterClaimTimerId: ReturnType<typeof setTimeout> | null = null

function isClaimRequestUrl(config: { url?: string; baseURL?: string }): boolean {
  const path = (config.baseURL || '') + (config.url || '')
  return path.includes('/claim')
}

/**
 * Auth/session endpoints own their 401s (wrong password, invalid code, dead refresh cookie,
 * logout of an already-dead session). Those must never open the session-expired modal or enter
 * the refresh-and-retry path — retrying a login with the same bad password is pointless, and
 * the modal firing during the modal's own cleanup logout re-opened it mid-navigation.
 */
function isAuthSessionEndpointUrl(url: string): boolean {
  return (
    url.includes('/user/refresh-token') ||
    url.includes('/user/clear-auth-cookies') ||
    url.includes('/user/logout') ||
    url.includes('/user/email/login') ||
    url.includes('/user/email/signup') ||
    url.includes('/user/email/forgot-password') ||
    url.includes('/user/firebase/')
  )
}

type RetriableRequestConfig = { url?: string; __retriedAfterSessionRecovery?: boolean }

function scheduleMissionRefetchAfterClaim() {
  if (missionRefetchAfterClaimTimerId != null) {
    clearTimeout(missionRefetchAfterClaimTimerId)
  }
  missionRefetchAfterClaimTimerId = setTimeout(() => {
    missionRefetchAfterClaimTimerId = null
    import('./state/mission/action').then(({ getMission }) => {
      ;(store.dispatch as AppDispatch)(getMission({}))
    })
  }, MISSION_REFETCH_AFTER_CLAIM_MS)
}

// Set up axios interceptor to add Authorization Bearer header and x-api-key to all requests
axios.interceptors.request.use(
  async (config) => {
    await alignSessionBeforeRequest(config)
    bindAuthenticatedWalletToAxiosRequest(config)

    // Add x-api-key header from environment variable
    const apiKey = process.env.REACT_APP_X_API_KEY
    if (apiKey && config.headers) {
      config.headers['x-api-key'] = apiKey
    }

    // Add Authorization Bearer token if available
    const token = getAuthToken()
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    const installId = getOrCreateClientInstallId()
    if (installId && config.headers) {
      config.headers['x-device-id'] = installId
    }

    const hwMac = getHardwareMacForRequest()
    if (hwMac && config.headers) {
      config.headers['x-mac-address'] = hwMac
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Set up axios response interceptor to handle network and server errors
axios.interceptors.response.use(
  (response) => {
    recordServerTimeFromResponseDateHeader(response.headers?.date)

    // SECURITY: Store only expiresAt (Unix timestamp), never the token.
    // Token stays in HttpOnly cookie - XSS cannot steal it.
    if (shouldPersistSessionExpiresAtFromBody(response)) {
      const expiresAt = (response.data as { expiresAt: number }).expiresAt
      setSessionExpiresAt(expiresAt)
      clearPendingReauthAfterSessionExpiry()
    }
    // Also update from X-Token-Expires-In header (backend sends when token < 1h left)
    const expiresInHeader = response.headers['x-token-expires-in']
    if (expiresInHeader) {
      const seconds = parseInt(expiresInHeader, 10)
      if (!isNaN(seconds) && seconds > 0) {
        setSessionExpiresAt(Math.floor(Date.now() / 1000) + seconds)
        clearPendingReauthAfterSessionExpiry()
      }
    }

    // Check if response indicates user is blocked
    if (
      response.data?.isBlocked === true ||
      response.data?.data?.isBlocked === true ||
      response.data?.user?.isBlocked === true
    ) {
      // Dispatch blocked status to Redux
      const userData = response.data?.user || response.data?.data || {}
      store.dispatch({
        type: GET_RESOURCES_SUCCESS,
        payload: { data: { ...userData, isBlocked: true } },
      })
      // Redirect to start page immediately (start page shows banned card)
      if (!window.location.pathname.startsWith('/start')) {
        setTimeout(() => {
          window.location.href = '/start'
        }, 0)
      }
    }

    if (isClaimRequestUrl(response.config)) {
      scheduleMissionRefetchAfterClaim()
    }

    return response
  },
  (error) => {
    // Handle network/server unreachable errors
    // When there's no response, it means the server couldn't be reached
    // This covers: network errors, server unreachable, timeouts, connection refused, etc.
    // if (!error.response) {
    //   // Only redirect if not already on error page
    //   if (!window.location.pathname.startsWith('/error/')) {
    //     // Use setTimeout to avoid blocking the error rejection
    //     setTimeout(() => {
    //       window.location.href = '/error/network'
    //     }, 0)
    //   }
    //   return Promise.reject(error)
    // }

    // Handle blocked users (403)
    if (error.response?.status === 403 && error.response?.data?.isBlocked === true) {
      // Dispatch blocked status to Redux
      const userData = error.response?.data?.data || {}
      store.dispatch({
        type: GET_RESOURCES_SUCCESS,
        payload: { data: { ...userData, isBlocked: true } },
      })
      // Redirect to start page (start page shows banned card)
      if (!window.location.pathname.startsWith('/start')) {
        setTimeout(() => {
          window.location.href = '/start'
        }, 0)
      }
      return Promise.reject(error)
    }

    // Handle 500 server errors
    if (error.response?.status === 500) {
      if (!window.location.pathname.startsWith('/error/')) {
        // Remember where the player was, so the error page's Retry can put them back. Without this
        // Retry could only reload `/error/500`, which just rendered the error page again.
        try {
          sessionStorage.setItem(
            ERROR_RETURN_TO_KEY,
            window.location.pathname + window.location.search,
          )
        } catch {
          /* private mode / storage blocked — Retry falls back to the land page */
        }
        setTimeout(() => {
          window.location.href = '/error/500'
        }, 0)
      }
      return Promise.reject(error)
    }

    // Handle 503 Service Unavailable (server down/maintenance)
    // if (error.response?.status === 503) {
    //   if (!window.location.pathname.startsWith('/error/')) {
    //     setTimeout(() => {
    //       window.location.href = '/error/network'
    //     }, 0)
    //   }
    //   return Promise.reject(error)
    // }

    // Handle 504 Gateway Timeout
    // if (error.response?.status === 504) {
    //   if (!window.location.pathname.startsWith('/error/')) {
    //     setTimeout(() => {
    //       window.location.href = '/error/network'
    //     }, 0)
    //   }
    //   return Promise.reject(error)
    // }

    // Handle 404 errors (optional - might want to let them fall through)
    // if (error.response?.status === 404) {
    //   if (!window.location.pathname.startsWith('/error/')) {
    //     window.location.href = '/error/404'
    //   }
    //   return Promise.reject(error)
    // }

    if (error.response?.status === 401) {
      const cfg = error.config as RetriableRequestConfig | undefined
      const url = String(cfg?.url || '')
      if (isAuthSessionEndpointUrl(url)) {
        return Promise.reject(error)
      }
      // A 401 usually means the access token died mid-session (24h expiry, or its IP/device-bound
      // `ctx` stopped matching after a network flip). The refresh token survives both, so try one
      // shared silent refresh and replay the request; only declare the session dead if that fails.
      if (!cfg || cfg.__retriedAfterSessionRecovery) {
        triggerSessionExpired()
        return Promise.reject(error)
      }
      return recoverSessionAfter401().then((recovered) => {
        if (!recovered) {
          triggerSessionExpired()
          return Promise.reject(error)
        }
        cfg.__retriedAfterSessionRecovery = true
        return axios.request(cfg)
      })
    }

    // For other errors, just reject normally
    return Promise.reject(error)
  },
)

export { refreshAccessToken, resetSessionRefreshDedupe, invalidateBootstrapRefresh }

/** Revoke session when possible, always clear HttpOnly auth cookies + client token cookie */
export const clearGameAuthSession = async (): Promise<void> => {
  resetSessionRefreshDedupe()
  try {
    await axios.post('/user/logout')
  } catch {
    /* still clear cookies below */
  }
  try {
    await axios.post('/user/clear-auth-cookies')
  } catch {
    /* best-effort */
  }
  clearAuthToken()
}

/** Logged-in email account: change password (requires current password). */
export const changeEmailPassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/user/email/change-password', {
    currentPassword,
    newPassword,
  })
  return response.data
}

/** Wallet account without email — request verification code to bind email + password. */
export const requestAddEmailToWallet = async (
  email: string,
  password: string,
): Promise<{ success: boolean; message?: string; retryAfter?: number }> => {
  const response = await axios.post('/user/email/add-to-wallet-request', { email, password })
  return response.data
}

export const resendAddEmailToWalletCode = async (
  email: string,
): Promise<{ success: boolean; message?: string; retryAfter?: number }> => {
  const response = await axios.post('/user/email/add-to-wallet-resend', { email })
  return response.data
}

export const verifyAddEmailToWallet = async (
  email: string,
  code: string,
): Promise<{ success: boolean; message?: string; mustReloginWithEmail?: boolean }> => {
  const response = await axios.post('/user/email/add-to-wallet-verify', { email, code })
  return response.data
}

export const handleStartPageEnter = async (walletAddress: string) => {
  const response = await axios.post('/user/start-enter', {
    walletAddress,
  })
  return response.data
}

export type PublicTournamentReward = {
  ranking?: number
  rankFrom?: number
  rankTo?: number
  label?: string
  rewardDescription?: string
  ecs?: number
  [key: string]: unknown
}

/** Bracket kind — selects the card artwork on the tournament hub. */
export type PublicTournamentType = 'pvp' | 'mvm'

export type PublicTournamentSummary = {
  id: string
  /** Monotonic numeric id (1, 2, …), not Mongo _id */
  tournamentId?: number | null
  /** Display season index when distinct from tournamentId (optional API field). */
  seasonNumber?: number | null
  /** Card title, e.g. "Legend Arena". Server supplies a default for legacy rows. */
  name: string
  type: PublicTournamentType
  /** Gems debited on join (0 = free entry). */
  entryFeeGems: number
  /** Advertised pool for display only — real payouts come from `rewards`. */
  prizePoolGems: number
  startsAt: string
  endsAt: string
  /** Joining closes here; groups lock and battles run from this instant until `endsAt`. */
  registrationEndsAt: string | null
  registrationOpen: boolean
  /** Admin override (0 = derived from the final joiner count). */
  groupCount: number
  /** Groups actually created; 0 until registration closes. */
  assignedGroupCount: number
  groupsAssigned: boolean
  rewards: PublicTournamentReward[]
  /** Signed-up wallets for this tournament (players + bots); from `GET /public/tournament/active` / tournament state. */
  participantCount?: number
}

function tournamentInstantToIso(v: unknown): string | null {
  if (v == null) return null
  if (typeof v === 'string' && v.length > 0) return v
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d.toISOString()
  }
  return null
}

/** Map API payload (including startAt/endAt aliases) to a stable summary for UI. */
function normalizePublicTournamentSummary(raw: unknown): PublicTournamentSummary | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const startsAt = tournamentInstantToIso(o.startsAt ?? o.startAt)
  const endsAt = tournamentInstantToIso(o.endsAt ?? o.endAt)
  if (!startsAt || !endsAt) return null
  const id = typeof o.id === 'string' ? o.id : ''
  const rewards = Array.isArray(o.rewards) ? (o.rewards as PublicTournamentReward[]) : []
  const tid = o.tournamentId
  const tournamentId =
    typeof tid === 'number' && Number.isFinite(tid)
      ? Math.floor(tid)
      : typeof tid === 'string' && /^\d+$/.test(tid)
        ? parseInt(tid, 10)
        : null
  const snRaw = o.seasonNumber ?? o.season
  const seasonNumber =
    typeof snRaw === 'number' && Number.isFinite(snRaw)
      ? Math.floor(snRaw)
      : typeof snRaw === 'string' && /^\d+$/.test(snRaw.trim())
        ? parseInt(snRaw.trim(), 10)
        : null
  const pcRaw = o.participantCount ?? o.totalJoiners ?? o.joinerCount
  let participantCount: number | undefined
  if (typeof pcRaw === 'number' && Number.isFinite(pcRaw) && pcRaw >= 0) {
    participantCount = Math.floor(pcRaw)
  } else if (typeof pcRaw === 'string' && /^\d+$/.test(pcRaw.trim())) {
    participantCount = parseInt(pcRaw.trim(), 10)
  }
  const nameRaw = typeof o.name === 'string' ? o.name.trim() : ''
  const typeRaw = typeof o.type === 'string' ? o.type.trim().toLowerCase() : ''
  return {
    id,
    startsAt,
    endsAt,
    rewards,
    tournamentId,
    seasonNumber,
    name: nameRaw || 'Legend Arena',
    type: typeRaw === 'mvm' ? 'mvm' : 'pvp',
    entryFeeGems: coerceGemField(o.entryFeeGems, DEFAULT_TOURNAMENT_ENTRY_FEE_GEMS),
    prizePoolGems: coerceGemField(o.prizePoolGems, 0),
    registrationEndsAt: tournamentInstantToIso(o.registrationEndsAt),
    registrationOpen: o.registrationOpen !== false,
    groupCount: coerceGemField(o.groupCount, 0),
    assignedGroupCount: coerceGemField(o.assignedGroupCount, 0),
    groupsAssigned: Boolean(o.groupsAssigned),
    ...(participantCount !== undefined ? { participantCount } : {}),
  }
}

/** Non-negative integer gem amount from an API field, or `fallback` when absent / unusable. */
function coerceGemField(raw: unknown, fallback: number): number {
  const n = Math.floor(Number(raw))
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

/** Public: current or upcoming tournament (not ended). `inBattleWindow` = now ∈ [startsAt, endsAt]. */
export const fetchPublicActiveTournament = async (): Promise<{
  active: boolean
  tournament: PublicTournamentSummary | null
  inBattleWindow: boolean
}> => {
  const { data } = await axios.get('/public/tournament/active')
  const tournament = data?.tournament ? normalizePublicTournamentSummary(data.tournament) : null
  const inBattleWindow = !!data?.inBattleWindow
  if (data?.active && tournament) {
    return { active: true, tournament, inBattleWindow }
  }
  return { active: false, tournament: null, inBattleWindow: false }
}

/**
 * Public: tournaments that have not started yet, soonest first — the hub's “Upcoming” rail.
 * Never throws; an empty list simply hides the rail.
 */
export const fetchPublicUpcomingTournaments = async (options?: {
  limit?: number
}): Promise<PublicTournamentSummary[]> => {
  try {
    const raw = Math.floor(Number(options?.limit))
    const limit = Number.isFinite(raw) && raw > 0 ? raw : TOURNAMENT_UPCOMING_CARD_COUNT
    const { data } = await axios.get('/public/tournament/upcoming', { params: { limit } })
    const items = (data?.data?.items ?? []) as unknown[]
    if (!Array.isArray(items)) return []
    return items
      .map((row) => normalizePublicTournamentSummary(row))
      .filter((x: PublicTournamentSummary | null): x is PublicTournamentSummary => x != null)
  } catch (error) {
    console.error('Error fetching upcoming tournaments:', error)
    return []
  }
}

/** Outcome of an already-played group meeting, from the viewer's side. */
export type TournamentGroupMemberResult = 'win' | 'loss' | 'draw' | null

/** One wallet inside the viewer's tournament battle group. */
export type TournamentGroupMember = {
  /** Rank inside the group, not the global tournament rank. */
  groupRank: number
  walletAddress: string
  nickname: string | null
  userLvl: number
  avatarUrl: string | null
  score: number
  wins: number
  losses: number
  ties: number
  matchesPlayed: number
  isViewer: boolean
  /** This viewer has already battled them — each opponent may be challenged only once. */
  played: boolean
  /**
   * How that battle ended, from this viewer's side. Null when they have not been played, or while
   * the battle is still running — so a live match is never mislabelled a draw.
   */
  playedResult: TournamentGroupMemberResult
  /** Seeded opponent rather than a real joiner. */
  isBot: boolean
  /** Live websocket presence. Offline players cannot receive a challenge. */
  online: boolean
  /** False for the viewer's own row and for opponents already played. */
  canChallenge: boolean
}

export type TournamentMyGroupPayload = {
  tournamentId: number | null
  /** False while registration is still open — groups do not exist yet. */
  groupsReady: boolean
  registrationOpen: boolean
  registrationEndsAt: string | null
  joined: boolean
  /** Total groups in the split (0 until assigned). */
  groupCount: number
  /** What the split would produce at the current joiner count (registration still open). */
  projectedGroupCount: number
  /** The viewer's 1-based group, or null if they did not join. */
  groupIndex: number | null
  members: TournamentGroupMember[]
}

/**
 * Authenticated: the viewer's battle group for a tournament plus that group's roster.
 * Never throws — an unavailable group simply renders as "not ready".
 */
export const postTournamentMyGroup = async (
  tournamentId?: number,
): Promise<TournamentMyGroupPayload | null> => {
  try {
    const body =
      typeof tournamentId === 'number' && Number.isFinite(tournamentId)
        ? { tournamentId: Math.floor(tournamentId) }
        : {}
    const { data } = await axios.post('/user/tournament/my-group', body)
    const d = data?.data
    if (!data?.success || !d || typeof d !== 'object') return null
    const o = d as Record<string, unknown>
    const num = (v: unknown, fallback = 0): number => {
      const n = Math.floor(Number(v))
      return Number.isFinite(n) && n >= 0 ? n : fallback
    }
    const groupIndexRaw = Math.floor(Number(o.groupIndex))
    const membersRaw = Array.isArray(o.members) ? (o.members as Record<string, unknown>[]) : []
    return {
      tournamentId: num(o.tournamentId) || null,
      groupsReady: Boolean(o.groupsReady),
      registrationOpen: Boolean(o.registrationOpen),
      registrationEndsAt: tournamentInstantToIso(o.registrationEndsAt),
      joined: Boolean(o.joined),
      groupCount: num(o.groupCount),
      projectedGroupCount: num(o.projectedGroupCount),
      groupIndex: Number.isFinite(groupIndexRaw) && groupIndexRaw >= 1 ? groupIndexRaw : null,
      members: membersRaw.map((m) => ({
        groupRank: num(m.groupRank),
        walletAddress: String(m.walletAddress ?? ''),
        nickname: typeof m.nickname === 'string' && m.nickname.trim() ? m.nickname.trim() : null,
        userLvl: num(m.userLvl, 1),
        avatarUrl:
          typeof m.avatarUrl === 'string' && m.avatarUrl.trim() ? m.avatarUrl.trim() : null,
        score: num(m.score),
        wins: num(m.wins),
        losses: num(m.losses),
        ties: num(m.ties),
        matchesPlayed: num(m.matchesPlayed),
        isViewer: Boolean(m.isViewer),
        played: Boolean(m.played),
        playedResult:
          m.playedResult === 'win' || m.playedResult === 'loss' || m.playedResult === 'draw'
            ? m.playedResult
            : null,
        isBot: Boolean(m.isBot),
        online: Boolean(m.online),
        canChallenge: Boolean(m.canChallenge),
      })),
    }
  } catch (error) {
    console.error('Error fetching tournament group:', error)
    return null
  }
}

function parsePvpMmrLeaderboardEntry(row: Record<string, unknown>): PvpMmrLeaderboardEntry {
  const tsRaw = row.tournamentScore
  const tournamentScore =
    tsRaw != null && tsRaw !== '' && Number.isFinite(Number(tsRaw))
      ? Math.floor(Number(tsRaw))
      : undefined
  const ecsEarnedNum = Number(row.ecsEarned)
  const ecsEarned = Number.isFinite(ecsEarnedNum) ? ecsEarnedNum : undefined
  const usdtEarnedNum = Number(row.usdtEarned)
  const usdtEarned = Number.isFinite(usdtEarnedNum) ? usdtEarnedNum : undefined

  const base: PvpMmrLeaderboardEntry = {
    rank: Number(row.rank) || 0,
    walletAddress: String(row.walletAddress ?? ''),
    nickName: (() => {
      const v = row.nickName
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    userLvl: (() => {
      const n = Number(row.userLvl)
      return Number.isFinite(n) && n > 0 ? n : 1
    })(),
    userExp: (() => {
      const n = Number(row.userExp)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpMmr: (() => {
      const n = Number(row.pvpMmr)
      return Number.isFinite(n) ? n : 0
    })(),
    pvpWins: (() => {
      const n = Number(row.pvpWins)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpLosses: (() => {
      const n = Number(row.pvpLosses)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpTies: (() => {
      const n = Number(row.pvpTies)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    avatarUrl: (() => {
      const v = row.avatarUrl
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
  }

  const expEcs = Number(row.expectedPlacementEcs)
  const expUsdt = Number(row.expectedPlacementUsdt)

  return {
    ...base,
    ...(row.isTrainingBot === true || row.isTrainingBot === 'true'
      ? { isTrainingBot: true as const }
      : {}),
    ...(tournamentScore !== undefined ? { tournamentScore } : {}),
    ...(ecsEarned !== undefined ? { ecsEarned } : {}),
    ...(usdtEarned !== undefined ? { usdtEarned } : {}),
    ...(Number.isFinite(expEcs) && expEcs >= 0 ? { expectedPlacementEcs: Math.floor(expEcs) } : {}),
    ...(Number.isFinite(expUsdt) && expUsdt >= 0 ? { expectedPlacementUsdt: expUsdt } : {}),
    ...(row.isOnline === true || row.isOnline === false ? { isOnline: row.isOnline === true } : {}),
  }
}

function parsePvpMmrLeaderboardListRow(row: Record<string, unknown>): PvpMmrLeaderboardListRow {
  if (row?.rowType === 'ellipsis') return { rowType: 'ellipsis' }
  return parsePvpMmrLeaderboardEntry(row)
}

/** Public: most recent ended week (id + rewards) for the result modal when it must not follow the “active” card. */
export const fetchPublicLastCompletedTournament = async (): Promise<{
  hasCompleted: boolean
  tournament: PublicTournamentSummary | null
}> => {
  try {
    const { data } = await axios.get('/public/tournament/last-completed')
    if (data?.hasCompleted && data?.tournament) {
      const t = normalizePublicTournamentSummary(data.tournament)
      return { hasCompleted: true, tournament: t }
    }
    return { hasCompleted: false, tournament: null }
  } catch (error: any) {
    console.error('Error fetching last completed tournament:', error)
    return { hasCompleted: false, tournament: null }
  }
}

export type PublicTournamentHistoryItem = {
  tournamentId: number
  seasonNumber: number
  endsAt: string
}

/** Public: past ended tournament weeks (newest first), for join page history. */
export const fetchPublicTournamentHistory = async (options?: {
  limit?: number
}): Promise<{
  success: boolean
  items: PublicTournamentHistoryItem[]
}> => {
  try {
    const limit = options?.limit
    const { data } = await axios.get('/public/tournament/history', {
      ...(typeof limit === 'number' && Number.isFinite(limit) && limit > 0
        ? { params: { limit: Math.min(40, Math.floor(limit)) } }
        : {}),
    })
    const d = data?.data
    if (data?.success && Array.isArray(d?.items)) {
      const items: PublicTournamentHistoryItem[] = d.items
        .map((row: Record<string, unknown>) => {
          const tid = row.tournamentId
          const sn = row.seasonNumber
          const endsAt = typeof row.endsAt === 'string' ? row.endsAt : null
          const id =
            typeof tid === 'number' && Number.isFinite(tid)
              ? Math.floor(tid)
              : typeof tid === 'string' && /^\d+$/.test(tid)
                ? parseInt(tid, 10)
                : null
          if (id == null || !endsAt) return null
          const s =
            typeof sn === 'number' && Number.isFinite(sn)
              ? Math.floor(sn)
              : typeof sn === 'string' && /^\d+$/.test(sn)
                ? parseInt(sn, 10)
                : id
          return { tournamentId: id, seasonNumber: s, endsAt }
        })
        .filter(
          (x: PublicTournamentHistoryItem | null): x is PublicTournamentHistoryItem => x != null,
        )
      return { success: true, items }
    }
    return { success: false, items: [] }
  } catch (error: any) {
    console.error('Error fetching tournament history:', error)
    return { success: false, items: [] }
  }
}

/** Public: tournament joiners ordered by score (same row shape as PvP MMR leaderboard + `tournamentScore`). */
export const fetchPublicTournamentLeaderboard = async (
  tournamentId?: number | null,
): Promise<{
  success: boolean
  data?: { entries: PvpMmrLeaderboardEntry[]; tournament?: PublicTournamentSummary | null }
  message?: string
}> => {
  try {
    const tid =
      tournamentId != null && Number.isFinite(tournamentId) ? Math.floor(tournamentId) : null
    const { data } = await axios.get('/public/tournament/leaderboard', {
      ...(tid != null && tid >= 1 ? { params: { tournamentId: tid } } : {}),
    })
    const d = data?.data
    if (data?.success && Array.isArray(d?.entries)) {
      const tRaw = d?.tournament
      const tournament =
        tRaw && typeof tRaw === 'object' ? normalizePublicTournamentSummary(tRaw) : null
      return {
        success: true,
        data: {
          entries: d.entries.map((row: Record<string, unknown>) =>
            parsePvpMmrLeaderboardEntry(row),
          ),
          tournament,
        },
      }
    }
    return { success: !!data?.success, message: data?.message }
  } catch (error: any) {
    console.error('Error fetching tournament leaderboard:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch tournament leaderboard',
    }
  }
}

/** Authenticated: join state for current/upcoming tournament. `inBattleWindow` = now ∈ [startsAt, endsAt]. */
export const fetchTournamentUserState = async (): Promise<{
  active: boolean
  inBattleWindow: boolean
  joined: boolean
  /** ECS from first-time tournament signup, claimable on enter-battle hub only */
  firstJoinEcsPending: number
  rank: {
    score: number
    streak: number
    usdtEarned: number
    ecsEarned: number
    matchesPlayed: number
    wins: number
    losses: number
    ties: number
  } | null
  /** Ordinal among joiners (1,1,3…); null when every joiner has score 0 */
  placementRank: number | null
  tournament: PublicTournamentSummary | null
}> => {
  const { data } = await axios.post('/user/tournament/state', {})
  const streakRaw = data.rank?.streak
  const streak =
    typeof streakRaw === 'number' && Number.isFinite(streakRaw)
      ? Math.min(5, Math.max(0, Math.floor(streakRaw)))
      : 0
  const usdtEarnedRaw = data.rank?.usdtEarned
  const usdtEarned =
    typeof usdtEarnedRaw === 'number' && Number.isFinite(usdtEarnedRaw) ? usdtEarnedRaw : 0
  const ecsEarnedRaw = data.rank?.ecsEarned
  const ecsEarned =
    typeof ecsEarnedRaw === 'number' && Number.isFinite(ecsEarnedRaw) ? ecsEarnedRaw : 0
  const matchesPlayedRaw = data.rank?.matchesPlayed
  const matchesPlayed =
    typeof matchesPlayedRaw === 'number' && Number.isFinite(matchesPlayedRaw)
      ? Math.max(0, Math.floor(matchesPlayedRaw))
      : 0
  const winsRaw = data.rank?.wins
  const wins =
    typeof winsRaw === 'number' && Number.isFinite(winsRaw) ? Math.max(0, Math.floor(winsRaw)) : 0
  const lossesRaw = data.rank?.losses
  const losses =
    typeof lossesRaw === 'number' && Number.isFinite(lossesRaw)
      ? Math.max(0, Math.floor(lossesRaw))
      : 0
  const tiesRaw = data.rank?.ties
  const ties =
    typeof tiesRaw === 'number' && Number.isFinite(tiesRaw) ? Math.max(0, Math.floor(tiesRaw)) : 0
  const pr = data.placementRank
  const placementRank =
    typeof pr === 'number' && Number.isFinite(pr) && pr >= 1 ? Math.floor(pr) : null
  const fjp = data.firstJoinEcsPending
  const firstJoinEcsPending =
    typeof fjp === 'number' && Number.isFinite(fjp) && fjp > 0 ? Math.floor(fjp) : 0
  return {
    active: !!data.active,
    inBattleWindow: !!data.inBattleWindow,
    joined: !!data.joined,
    firstJoinEcsPending,
    rank:
      data.rank && typeof data.rank.score === 'number'
        ? {
            score: data.rank.score,
            streak,
            usdtEarned,
            ecsEarned,
            matchesPlayed,
            wins,
            losses,
            ties,
          }
        : null,
    placementRank,
    tournament: data.tournament ? normalizePublicTournamentSummary(data.tournament) : null,
  }
}

export const postTournamentJoin = async (): Promise<{
  success: boolean
  joined?: boolean
  alreadyJoined?: boolean
  /** 400: wallet cannot cover `Tournament.entryFeeGems`. Nothing was debited. */
  insufficientGems?: boolean
  /** 400: the registration window closed — groups are locked, so no late entry. */
  registrationClosed?: boolean
  /** Entry price in Gems — echoed on success and on the insufficient-balance error. */
  entryFeeGems?: number
  /** Gems actually debited by this call (0 when already joined or entry is free). */
  gemsCharged?: number
  /** Wallet balance after the debit, for the resource HUD. */
  user?: Record<string, unknown>
  /** Present after first-time tournament signup; ECS is credited when claimed on the enter-battle hub. */
  firstJoinEcsPending?: number
}> => {
  try {
    const { data } = await axios.post('/user/tournament/join', {})
    const pendingRaw = data.firstJoinEcsPending
    const firstJoinEcsPending =
      typeof pendingRaw === 'number' && Number.isFinite(pendingRaw) && pendingRaw > 0
        ? pendingRaw
        : undefined
    const feeRaw = Number(data.entryFeeGems)
    const chargedRaw = Number(data.gemsCharged)
    return {
      success: !!data.success,
      joined: !!data.joined,
      alreadyJoined: !!data.alreadyJoined,
      ...(Number.isFinite(feeRaw) ? { entryFeeGems: Math.max(0, Math.floor(feeRaw)) } : {}),
      ...(Number.isFinite(chargedRaw) ? { gemsCharged: Math.max(0, Math.floor(chargedRaw)) } : {}),
      ...(data.user && typeof data.user === 'object'
        ? { user: data.user as Record<string, unknown> }
        : {}),
      ...(firstJoinEcsPending !== undefined ? { firstJoinEcsPending } : {}),
    }
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { code?: string; entryFeeGems?: unknown } } }
    if (ax?.response?.data?.code === 'TOURNAMENT_REGISTRATION_CLOSED') {
      return { success: false, registrationClosed: true }
    }
    if (ax?.response?.data?.code === 'TOURNAMENT_INSUFFICIENT_GEMS') {
      const feeRaw = Number(ax.response?.data?.entryFeeGems)
      return {
        success: false,
        insufficientGems: true,
        entryFeeGems: Number.isFinite(feeRaw)
          ? Math.max(0, Math.floor(feeRaw))
          : DEFAULT_TOURNAMENT_ENTRY_FEE_GEMS,
      }
    }
    throw err
  }
}

/** Credit pending first-tournament signup ECS and clear pending flag. */
export const postTournamentClaimFirstJoinEcs = async (): Promise<{
  success: boolean
  claimed: boolean
  ecsClaimed?: number
  firstJoinEcsPending?: number
  user?: Record<string, unknown>
  message?: string
  /** 400: nothing to claim, or already claimed (incl. legacy / missing field) */
  notClaimable?: boolean
}> => {
  try {
    const { data } = await axios.post('/user/tournament/claim-first-join-ecs', {})
    const u = data.user
    return {
      success: !!data.success,
      claimed: !!data.claimed,
      ...(typeof data.ecsClaimed === 'number' && data.ecsClaimed > 0
        ? { ecsClaimed: data.ecsClaimed }
        : {}),
      ...(typeof data.firstJoinEcsPending === 'number'
        ? { firstJoinEcsPending: data.firstJoinEcsPending }
        : {}),
      ...(u && typeof u === 'object' ? { user: u as Record<string, unknown> } : {}),
      message: data.message,
    }
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number; data?: { code?: string; message?: string } } }
    if (
      ax?.response?.status === 400 &&
      ax?.response?.data?.code === 'TOURNAMENT_FIRST_JOIN_NOT_CLAIMABLE'
    ) {
      return {
        success: false,
        claimed: false,
        notClaimable: true,
        message: ax.response.data?.message,
      }
    }
    throw err
  }
}

export type PostTournamentClaimPlacementRewardResult = {
  success: boolean
  claimed?: boolean
  alreadyCredited?: boolean
  ecs?: number
  usdt?: number
  user?: { ecsTokenAmount?: number; [k: string]: unknown }
  code?: string
}

/**
 * Claim end-of-tournament **placement** ECS/USDT; idempotent.
 */
/** Read-only: whether the signed-in user already claimed placement reward for the ended `tournamentId`. */
export const postTournamentPlacementClaimStatus = async (
  tournamentId: number,
): Promise<{
  success: boolean
  placementRewardClaimed?: boolean
  notParticipant?: boolean
}> => {
  try {
    const { data } = await axios.post<{
      success: boolean
      placementRewardClaimed?: boolean
      notParticipant?: boolean
    }>('/user/tournament/placement-claim-status', { tournamentId: Math.floor(tournamentId) })
    return {
      success: !!data?.success,
      ...(typeof data?.placementRewardClaimed === 'boolean'
        ? { placementRewardClaimed: data.placementRewardClaimed }
        : {}),
      ...(data?.notParticipant ? { notParticipant: true } : {}),
    }
  } catch {
    return { success: false }
  }
}

export const postTournamentClaimPlacementReward = async (
  tournamentId: number,
): Promise<PostTournamentClaimPlacementRewardResult> => {
  try {
    const { data } = await axios.post<
      PostTournamentClaimPlacementRewardResult & { message?: string }
    >('/user/tournament/claim-placement-reward', { tournamentId: Math.floor(tournamentId) })
    const parseNum = (v: unknown) => {
      if (typeof v === 'number' && Number.isFinite(v)) return v
      if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v)
        if (Number.isFinite(n)) return n
      }
      return undefined
    }
    const ecsN = parseNum((data as { ecs?: unknown }).ecs)
    const usdtN = parseNum((data as { usdt?: unknown }).usdt)
    return {
      success: !!data?.success,
      claimed: data?.claimed,
      alreadyCredited: data?.alreadyCredited,
      ...(ecsN !== undefined ? { ecs: ecsN } : {}),
      ...(usdtN !== undefined ? { usdt: usdtN } : {}),
      ...(data?.user && typeof data.user === 'object'
        ? { user: data.user as PostTournamentClaimPlacementRewardResult['user'] }
        : {}),
    }
  } catch (err: unknown) {
    const ax = err as { response?: { status?: number; data?: { code?: string; message?: string } } }
    return {
      success: false,
      code: ax?.response?.data?.code,
    }
  }
}

/**
 * Authenticated: the reward-eligible ladder slice (ranks 1…`leaderboardSliceMaxRank`, currently 100)
 * plus viewer rank and placement claim status for a season (`tournamentId`).
 */
export type TournamentSeasonViewerPayload = {
  walletAddress: string
  viewerTournamentRank: number | null
  notParticipant: boolean
  placementRewardClaimed: boolean
  placementPayout: {
    at?: string
    placementRank?: number
    ecs?: number
    usdt?: number
  } | null
  /** Resolved display amounts (matches leaderboard rows / placement rules); use for Claim CTA suffix when set. */
  displayPlacementEcs?: number
  displayPlacementUsdt?: number
  /** Set only when the viewer ranks past the slice; otherwise they appear in `entriesTopRanked`. */
  rowWhenOutsideTopRanked: PvpMmrLeaderboardEntry | null
}

export type TournamentSeasonResultPayload = {
  tournament: PublicTournamentSummary
  rewards: PublicTournamentReward[]
  entriesTopRanked: PvpMmrLeaderboardEntry[]
  /** Highest rank present in `entriesTopRanked` (mirrors the server claim cap). */
  leaderboardSliceMaxRank: number
  viewer: TournamentSeasonViewerPayload
}

export const postTournamentSeasonResult = async (
  tournamentId: number,
): Promise<{ success: boolean; data?: TournamentSeasonResultPayload }> => {
  try {
    const { data } = await axios.post<{ success?: boolean; data?: Record<string, unknown> }>(
      '/user/tournament/season-result',
      { tournamentId: Math.floor(tournamentId) },
    )
    const d = data?.data
    if (!data?.success || !d || typeof d !== 'object') {
      return { success: !!data?.success }
    }
    const entriesRaw = (d as { entriesTopRanked?: unknown }).entriesTopRanked
    const rewardsRaw = (d as { rewards?: unknown }).rewards
    const viewerRaw = (d as { viewer?: Record<string, unknown> }).viewer
    const tourRaw = (d as { tournament?: unknown }).tournament
    const sliceMaxRaw = Number((d as { leaderboardSliceMaxRank?: unknown }).leaderboardSliceMaxRank)
    const leaderboardSliceMaxRank =
      Number.isFinite(sliceMaxRaw) && sliceMaxRaw >= 1
        ? Math.floor(sliceMaxRaw)
        : TOURNAMENT_PLACEMENT_CLAIM_MAX_RANK
    const entriesTopRanked = Array.isArray(entriesRaw)
      ? (entriesRaw as Record<string, unknown>[]).map((row) => parsePvpMmrLeaderboardEntry(row))
      : []
    const rewards: PublicTournamentReward[] = Array.isArray(rewardsRaw)
      ? (rewardsRaw as PublicTournamentReward[])
      : []
    const tournament =
      tourRaw && typeof tourRaw === 'object'
        ? normalizePublicTournamentSummary(tourRaw as Record<string, unknown>)
        : null
    if (!tournament) {
      return { success: false }
    }
    const vr = viewerRaw ?? {}
    const rowAbove = vr.rowWhenOutsideTopRanked
    const rowWhenOutsideTopRanked =
      rowAbove && typeof rowAbove === 'object'
        ? parsePvpMmrLeaderboardEntry(rowAbove as Record<string, unknown>)
        : null
    const pp = vr.placementPayout
    const viewer: TournamentSeasonViewerPayload = {
      walletAddress: String(vr.walletAddress ?? ''),
      viewerTournamentRank: (() => {
        const r = vr.viewerTournamentRank
        if (typeof r === 'number' && Number.isFinite(r) && r >= 1) return Math.floor(r)
        return null
      })(),
      notParticipant: !!vr.notParticipant,
      placementRewardClaimed: !!vr.placementRewardClaimed,
      placementPayout:
        pp && typeof pp === 'object'
          ? {
              at:
                typeof (pp as { at?: unknown }).at === 'string'
                  ? (pp as { at: string }).at
                  : undefined,
              placementRank: (() => {
                const pr = (pp as { placementRank?: unknown }).placementRank
                return typeof pr === 'number' && Number.isFinite(pr) ? pr : undefined
              })(),
              ecs: (() => {
                const x = (pp as { ecs?: unknown }).ecs
                return typeof x === 'number' && Number.isFinite(x) ? x : undefined
              })(),
              usdt: (() => {
                const x = (pp as { usdt?: unknown }).usdt
                return typeof x === 'number' && Number.isFinite(x) ? x : undefined
              })(),
            }
          : null,
      displayPlacementEcs: (() => {
        const x = vr.displayPlacementEcs
        return typeof x === 'number' && Number.isFinite(x) ? Math.max(0, Math.floor(x)) : undefined
      })(),
      displayPlacementUsdt: (() => {
        const x = vr.displayPlacementUsdt
        return typeof x === 'number' && Number.isFinite(x) ? Math.max(0, x) : undefined
      })(),
      rowWhenOutsideTopRanked,
    }
    return {
      success: true,
      data: {
        tournament,
        rewards,
        entriesTopRanked,
        leaderboardSliceMaxRank,
        viewer,
      },
    }
  } catch {
    return { success: false }
  }
}

export const getProfile = async (walletAddress: string, character: string) => {
  const data = (
    await axios.post('/user/profile', {
      walletAddress,
      character,
    })
  ).data

  const user = data.user

  // Dispatch user data to Redux including isBlocked
  store.dispatch({
    type: GET_RESOURCES_SUCCESS,
    payload: { data: user },
  })

  if (user?.walletAddress) {
    global.walletAddress = user.walletAddress
  }
  global.userRef = user.userRef
  global.wall = user.wall
  global.characters = user.characters
  global.level = user.level
  global.hunterLevel = user.hunterLevel
  global.resource = user.resource
  global.water = user.water
  global.referralCode = user.userRef
  global.referralCodeStatus = user.enterCodeStatus || !!user.referrer
  global.ref = user.referrer
  global.weapons = user.weapons
  store.dispatch(setRememberCode(user.enterCodeStatus || !!user.referrer))
  store.dispatch(setNickname(user.nickName))
  global.skull = user.skull
}
export const referalAdd = async () => {
  await axios.post('/user/referal', {
    guest: global.userRef,
    introducer: global.ref,
  })
}

export const loggedInUser = async () => {
  await axios.post('/user/logged-in', {
    walletAddress: global.walletAddress,
  })
}

export const itemRevive = async (
  walletAddress: string,
  character: string,
  characterID: string,
  item: string,
  cb: Function,
) => {
  const data = (
    await axios.post('/user/item/revive', {
      walletAddress,
      character,
      characterID,
      item,
    })
  ).data

  const user = data.user

  const currentCharacter = user.characters.filter(
    (character: any) => character._id.toString() === characterID,
  )[0]
  cb({
    characters: currentCharacter,
    purchase: data.purchase,
    embed: data.embed,
    currentCharacterName: character,
    currentCharacterID: characterID, // Include characterID so it's not lost
  })
}

// ✨ NEW: Fetch battle configurations from backend
export const getBattleConfigurations = async () => {
  const response = await axios.get('/pve/battle_configs')
  return response.data.configurations
}

export const getBattleConfigByRound = async (roundNumber: number) => {
  const response = await axios.get(`/pve/battle_configs/round/${roundNumber}`)
  return response.data.configuration
}

// ✨ NEW: PVE Battle Execution API (Server-Side Combat)
export const startPVEBattle = async (walletAddress: string, roundNumber: number) => {
  const response = await axios.post('/pve/start_battle', { walletAddress, roundNumber })
  return response.data
}

export const startRandomPVEBattle = async (walletAddress: string, characterInstanceId: string) => {
  const response = await axios.post('/pve/start_random_battle', {
    walletAddress,
    characterInstanceId,
  })
  return response.data
}

export const playerAction = async (battleId: string, actionCode: string, walletAddress: string) => {
  const response = await axios.post('/pve/player_attack', {
    battleId,
    action_code: actionCode,
    walletAddress,
  })
  return response.data
}

export const enemyAction = async (battleId: string) => {
  const response = await axios.post('/pve/enemy_attack', { battleId })
  return response.data
}

// ✨ NEW: Character Instance Management API
export const createCharacter = async (
  walletAddress: string,
  characterId: string,
  rarity: string = 'common',
) => {
  const response = await axios.post('/characters/create', { walletAddress, characterId, rarity })
  return response.data
}

export const getUserCharacters = async (
  walletAddress: string,
  characterType?: 'hero' | 'worker',
  _fetchAllCastlesUnused?: boolean,
) => {
  // characterType 'hero': adult CharacterInstance only (excludes origin 'child'), no workers — use for PvP/combat UIs.
  // undefined: heroes (including children) + workers — full inventory (filter `type === 'hero'` client-side for espionage, etc.).
  // The backend fetches all castles when nativeCastle is not provided.
  const body: any = { walletAddress, characterType }
  // Note: We don't pass nativeCastle here - backend will fetch all characters from all castles
  // This ensures PvP can access characters from main castle (0) and castles 1-4
  const response = await axios.post(`/characters/fetch-characters`, body)
  const payload = response.data
  if (payload?.success && Array.isArray(payload.data)) {
    return { ...payload, data: filterEphemeralPvpRosterCharacters(payload.data) }
  }
  return payload
}

export const getLockedCharactersFromCastles = async (walletAddress: string) => {
  const response = await axios.post(`/characters/fetch-locked-characters-from-castles`, {
    walletAddress,
  })
  return response.data
}

export const getUserProgress = async (walletAddress: string) => {
  const response = await axios.post(`/user/profile`, { walletAddress })
  // Backend returns regionCompletion with both regions object and simplified flags
  const regionCompletion = response.data.regionCompletion || {}
  const castleBuilding = response.data.castleBuilding || {}

  // Derive unlockedCastleBuildings from CastleBuilding isLocked field
  // Building is unlocked if isLocked is false
  const getUnlockedCastleBuildings = () => {
    return {
      castle1: !(castleBuilding.castle1?.buildings?.bakery?.isLocked ?? true),
      castle2: !(castleBuilding.castle2?.buildings?.hunting?.isLocked ?? true),
      castle3: !(castleBuilding.castle3?.buildings?.training?.isLocked ?? true),
      castle4: !(castleBuilding.castle4?.buildings?.hospital?.isLocked ?? true),
    }
  }

  // Extract region completion status from RegionCompletion model
  // Backend returns: { regions: {...}, region1: bool, region2: bool, ... }
  // Use RegionCompletion as primary source of truth for region completion
  const regionsData = regionCompletion.regions || {}
  const regionCompletionData = {
    region1: regionCompletion.region1 || regionsData.region1?.completed || false,
    region2: regionCompletion.region2 || regionsData.region2?.completed || false,
    region3: regionCompletion.region3 || regionsData.region3?.completed || false,
    region4: regionCompletion.region4 || regionsData.region4?.completed || false,
    // Include detailed region data with highestRoundCompleted for each region
    regions: {
      region1: {
        completed: regionCompletion.region1 || regionsData.region1?.completed || false,
        highestRoundCompleted: regionsData.region1?.highestRoundCompleted || 0,
      },
      region2: {
        completed: regionCompletion.region2 || regionsData.region2?.completed || false,
        highestRoundCompleted: regionsData.region2?.highestRoundCompleted || 0,
      },
      region3: {
        completed: regionCompletion.region3 || regionsData.region3?.completed || false,
        highestRoundCompleted: regionsData.region3?.highestRoundCompleted || 0,
      },
      region4: {
        completed: regionCompletion.region4 || regionsData.region4?.completed || false,
        highestRoundCompleted: regionsData.region4?.highestRoundCompleted || 0,
      },
    },
  }

  return {
    campaignProgress: response.data.user?.campaignProgress || {
      highestRoundCompleted: 0,
      lastPlayedRound: 0,
      currentArea: 'forest',
    },
    unlockedCastleBuildings: getUnlockedCastleBuildings(),
    kingCharacter: response.data.kingCharacter || null,
    regionCompletion: regionCompletionData,
    castleBuilding: {
      castle1: {
        locked: castleBuilding.castle1?.locked !== false, // Default to locked
        buildings: castleBuilding.castle1?.buildings || {},
      },
      castle2: {
        locked: castleBuilding.castle2?.locked !== false,
        buildings: castleBuilding.castle2?.buildings || {},
      },
      castle3: {
        locked: castleBuilding.castle3?.locked !== false,
        buildings: castleBuilding.castle3?.buildings || {},
      },
      castle4: {
        locked: castleBuilding.castle4?.locked !== false,
        buildings: castleBuilding.castle4?.buildings || {},
      },
      castle5: {
        locked: castleBuilding.castle5?.locked !== false,
        buildings: castleBuilding.castle5?.buildings || {},
      },
    },
  }
}

// ✨ Unlock castle building (100 ECS cost + character reward)
export const unlockCastleBuilding = async (walletAddress: string, castleNumber: number) => {
  const response = await axios.post('/land/castle/unlock-building', {
    walletAddress,
    castleNumber,
  })
  return response.data
}

// ✨ Get castle building status for a specific castle
export const getCastleBuildingsStatus = async (walletAddress: string, castleId: number) => {
  const response = await axios.post('/land/castle/buildings/status', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const getAvailableCharactersForCastle = async (walletAddress: string, castleId: number) => {
  const response = await axios.post('/land/castle/available-characters', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const transferCharacterBetweenCastles = async (
  walletAddress: string,
  characterId: string | string[],
  fromCastle: number,
  toCastle: number,
  fromBuilding: string,
  toBuilding: string,
  order: number = 0,
) => {
  const isMultiple = Array.isArray(characterId)
  const response = await axios.post('/land/castle/transfer-character', {
    walletAddress,
    ...(isMultiple ? { characterIds: characterId } : { characterId }),
    fromCastle,
    toCastle,
    fromBuilding,
    toBuilding,
    order,
  })
  return response.data
}

// completeCharacterTransfers removed - transfers are now immediate, no completion needed

export const addCharacterToCastleBuilding = async (
  walletAddress: string,
  castleId: number,
  building: string,
  characterId: string,
  order: number = 0,
) => {
  const response = await axios.post('/land/castle/add-character', {
    walletAddress,
    castleId,
    building,
    characterId,
    order,
  })
  return response.data
}

export const removeCharacterFromCastleBuilding = async (
  walletAddress: string,
  castleId: number,
  building: string,
  characterId: string,
) => {
  const response = await axios.post('/land/castle/remove-character', {
    walletAddress,
    castleId,
    building,
    characterId,
  })
  return response.data
}

// getPendingTransfers removed - transfers are now immediate, no tracking needed

export const checkCastleBuildingUpgradeable = async (
  walletAddress: string,
  castleId: number,
  building: string,
) => {
  const response = await axios.post('/land/castle/check-upgradeable', {
    walletAddress,
    castleId,
    building,
  })
  return response.data
}

export const upgradeCastleBuilding = async (
  walletAddress: string,
  castleId: number,
  building: string,
  upgradeType: 'ecs' | 'resources' = 'resources',
) => {
  const response = await axios.post('/land/castle/upgrade-building', {
    walletAddress,
    castleId,
    building,
    upgradeType,
  })
  return response.data
}

export const repairCastleBuilding = async (
  walletAddress: string,
  castleId: number,
  building: string,
  material: string,
) => {
  const response = await axios.post('/land/castle/repair-building', {
    walletAddress,
    castleId,
    building,
    material,
  })
  return response.data
}

export const getCastleRepairRequirements = async (
  walletAddress: string,
  castleId: number,
  building: string,
) => {
  const response = await axios.post('/land/castle/repair-requirements', {
    walletAddress,
    castleId,
    building,
  })
  return response.data
}

export const getCharacterById = async ({
  characterId,
  walletAddress,
}: {
  characterId: string
  walletAddress: string
}) => {
  const response = await axios.post(`/characters/fetch-character-by-id`, {
    characterId,
    walletAddress,
  })
  return response.data
}

export const deleteCharacter = async (characterInstanceId: string) => {
  const response = await axios.delete(`/characters/${characterInstanceId}`)
  return response.data
}

export const upgradeAbility = async (
  walletAddress: string,
  characterInstanceId: string,
  abilityType: string,
) => {
  const response = await axios.post('/characters/upgrade-ability', {
    walletAddress,
    characterInstanceId,
    abilityType,
  })
  return response.data
}

export type ReleaseCharacterForBattleResult = {
  released: boolean
  /** Which assignments the cancel actually undid; empty when only a stale `isUsing` flag was cleared. */
  clearedActivities: Array<
    'espionage_training' | 'diplomacy_training' | 'envoy_mission' | 'building_assignment'
  >
  clearedStaleLockOnly?: boolean
  /** Building slots vacated (`castleId: 0` is the main land) — refetch land status when non-empty. */
  vacatedBuildings?: Array<{ castleId: number; building: string }>
  /** Present when a hospital slot was vacated: the heal banked on the way out. */
  currentHp?: number
  maxHp?: number
  character?: { _id: string; isUsing: boolean; isLocked: boolean }
}

/**
 * "Cancel" from the MvM character-selection modal: free a hero's non-combat assignment so they can be
 * fielded in this battle right away. Rejects (409) with a human-readable reason when something real
 * still holds the hero — a live march or hex battle, a spy mission, or a mining-factory shift.
 */
export const releaseCharacterForBattle = async (
  walletAddress: string,
  characterInstanceId: string,
): Promise<ReleaseCharacterForBattleResult> => {
  const response = await axios.post('/characters/release-for-battle', {
    walletAddress,
    characterInstanceId,
  })
  return response.data?.data ?? response.data
}

export type StartPveBattleOptions = {
  /** World-map barbarian raid: server verifies village tier and skips campaign round gates */
  fromBarbarianRaid?: boolean
  barbarianVillageId?: string
  barbarianRaidWorldId?: string
  /** Land-page daily barbarians: server checks the safe window and today's round, then counts the win */
  fromLandBarbarian?: boolean
}

// Update startPVEBattle to accept optional characterInstanceId
export const startPVEBattleWithCharacter = async (
  walletAddress: string,
  roundNumber: number,
  characterInstanceId?: string,
  options?: StartPveBattleOptions,
) => {
  const body: Record<string, unknown> = {
    walletAddress,
    roundNumber,
    characterInstanceId,
  }
  if (options?.fromBarbarianRaid) {
    body.fromBarbarianRaid = true
    if (options.barbarianVillageId) body.barbarianVillageId = options.barbarianVillageId
    if (options.barbarianRaidWorldId) body.barbarianRaidWorldId = options.barbarianRaidWorldId
  }
  if (options?.fromLandBarbarian) {
    body.fromLandBarbarian = true
  }
  const response = await axios.post('/pve/start_battle', body)
  return response.data
}

// ✨ Land-page daily barbarians
export type BarbarianRaidReport = {
  raidedAt: string
  remainingHp: number
  maxHp: number
  totalDamage: number
  shielded: boolean
  damages: Array<{ building: string; damage: number }>
}

export type BarbarianDailyStatus = {
  eligible: boolean
  minLevel: number
  /** UTC day (YYYY-MM-DD) this status belongs to. */
  dayKey: string
  serverNow: string
  /** 12:00 UTC: end of the safe window, when undefeated barbarians raid. */
  raidAt: string
  /** 00:00 UTC next day: barbarians reset. */
  nextResetAt: string
  maxHp: number
  claimReward: number
  status: 'ineligible' | 'active' | 'defeated' | 'raided'
  hp: number
  wins: number
  rewardClaimed: boolean
  defeatedAt: string | null
  raidedAt: string | null
  raidReport: BarbarianRaidReport | null
  era: number | null
  firstRound: number | null
  lastRound: number | null
  /** Round of the next barbarian battle (null unless status is 'active'). */
  nextRound: number | null
  enemyStatMultiplier: number
}

export const getBarbarianDailyStatus = async (
  walletAddress: string
): Promise<{ barbarian?: BarbarianDailyStatus; success?: boolean; message?: string }> => {
  const response = await axios.post('/land/barbarian/status', { walletAddress })
  return response.data
}

export const claimBarbarianDailyReward = async (
  walletAddress: string
): Promise<{ success: boolean; message?: string; reward?: number; ecsTokenAmount?: number | null }> => {
  const response = await axios.post('/land/barbarian/claim', { walletAddress })
  return response.data
}

// ✨ Marketplace API
export const getMarketplaceItems = async () => {
  const response = await axios.get('/market/auction/fetch')
  return response.data
}

/** Global recently-sold auctions (price feed) for the Auction tab's History view. */
export const getMarketplaceHistory = async () => {
  const response = await axios.get('/market/auction/history')
  return response.data
}

export const buyMarketplaceItem = async (walletAddress: string, auctionId: string) => {
  const response = await axios.post('/market/auction/buy', {
    walletAddress,
    auctionId,
  })
  return response.data
}

// Leaderboard API
export const getLeaderboard = async (
  limit: number = 100,
  sortBy: 'userLvl' | 'userScore' = 'userLvl',
  skip: number = 0,
  walletAddress?: string,
) => {
  const response = await axios.get('/public/leaderboard', {
    params: {
      limit,
      sortBy,
      skip,
      ...(walletAddress?.trim() ? { walletAddress: walletAddress.trim() } : {}),
    },
  })
  return response.data
}

/** Public top PvP MMR rows (no wallet/auth). Same ordering as POST /pvp/mmr-leaderboard. */
export const getPublicPvpMmrLeaderboard = async (
  limit: number = 100,
  skip: number = 0,
): Promise<{
  success: boolean
  leaderboard?: Array<{
    rank: number
    walletAddress: string
    nickName: string | null
    userLvl: number
    pvpMmr: number
    avatarUrl?: string | null
  }>
  total?: number
  message?: string
}> => {
  try {
    const response = await axios.get('/public/pvp-mmr-leaderboard', { params: { limit, skip } })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } }
    console.error('Error fetching public PvP MMR leaderboard:', error)
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to fetch PvP MMR leaderboard',
    }
  }
}

/** Admin news locales — same order as admin panel `NEWS_LOCALE_CODES`. */
export const NEWS_LOCALE_CODES = ['en', 'ar', 'ja', 'ru', 'zh'] as const

export type GameNewsLocaleCode = (typeof NEWS_LOCALE_CODES)[number]

export type GameNewsLocaleBundle = { title?: string; description?: string }

export type GameNewsLocales = Partial<Record<GameNewsLocaleCode, GameNewsLocaleBundle>>

export interface LatestGameNewsPayload {
  success: boolean
  message?: string
  news: null | {
    id: string
    createdAt: string
    updatedAt?: string
    locales: GameNewsLocales
  }
}

/** GET /public/news/latest — latest row from `admin_news` (no auth). */
export const fetchLatestGameNews = async (): Promise<LatestGameNewsPayload> => {
  const response = await axios.get<LatestGameNewsPayload>('/public/news/latest')
  return response.data
}

// LSS Leaderboard API
export interface LSSLeaderboardEntry {
  rank: number
  walletAddress: string
  nickName: string
  userLvl: number
  avatar: any
  lssPoints: number
}

export const getLSSLeaderboard = async (
  limit: number = 100,
): Promise<{
  success: boolean
  leaderboard?: LSSLeaderboardEntry[]
  seasonNumber?: number
  message?: string
}> => {
  const response = await axios.get('/public/lss-leaderboard', {
    params: { limit },
  })
  return response.data
}

export const getUserLSSStats = async (
  walletAddress: string,
): Promise<{
  success: boolean
  walletAddress?: string
  lssPoints?: number
  currentSeasonNumber?: number
  message?: string
}> => {
  const response = await axios.post('/public/lss-stats', {
    walletAddress,
  })
  return response.data
}

export const swapMarketplaceItem = async (
  walletAddress: string,
  auctionId: string,
  targetItems: string[],
) => {
  const response = await axios.post('/market/auction/swap', {
    walletAddress,
    auctionId,
    targetItems,
  })
  return response.data
}

export const sellMarketplaceItem = async (
  walletAddress: string,
  itemType: string,
  itemId: string,
  price: number,
) => {
  const response = await axios.post('/market/auction/sell', {
    walletAddress,
    itemId,
    itemType,
    price,
  })

  // Check if the operation failed (data: false)
  if (response.data && response.data.data === false) {
    throw new Error(response.data.message || 'Failed to sell item')
  }

  return response.data.auction
}

export const cancelMarketplaceItem = async (walletAddress: string, auctionId: string) => {
  const response = await axios.post('/market/auction/cancel', {
    walletAddress,
    auctionId,
  })
  return response.data
}

// Notification APIs
export const getNotifications = async (walletAddress: string, type?: string) => {
  const response = await axios.post('/notifications/fetch', {
    walletAddress,
    type,
  })
  return response.data
}

export const markNotificationAsRead = async (walletAddress: string, notificationId: string) => {
  const response = await axios.post('/notifications/mark-read', {
    walletAddress,
    notificationId,
  })
  return response.data
}

export const markAllNotificationsAsRead = async (walletAddress: string) => {
  const response = await axios.post('/notifications/mark-all-read', {
    walletAddress,
  })
  return response.data
}

export const deleteNotification = async (walletAddress: string, notificationId: string) => {
  const response = await axios.post('/notifications/delete', {
    walletAddress,
    notificationId,
  })
  return response.data
}

export const deleteAllNotifications = async (walletAddress: string) => {
  const response = await axios.post('/notifications/delete-all', {
    walletAddress,
  })
  return response.data
}

// Land Page
export const getLandStatus = async (walletAddress: string) => {
  return dedupeInFlight(`land:building:status:${walletAddress}`, async () => {
    const response = await axios.post('/land/buiding/status', {
      walletAddress,
    })
    return {
      landStatus: response.data.landStatus,
      supportState: response.data.supportState,
      empirePeriod: response.data.empirePeriod,
      kingdomTechEffects: response.data.kingdomTechEffects,
    }
  })
}

/** GET /api/mission - mission status and progress. Optional ?day=N. Returns { mission, missionProgress, walletAddress }. */
export const getMissionApi = async (params?: { day?: number }) => {
  const response = await axios.get('/mission', { params: params ?? {} })
  const data = response.data?.data ?? response.data
  return {
    mission: data?.mission ?? null,
    missionProgress: data?.missionProgress ?? null,
    walletAddress: data?.walletAddress ?? '',
  }
}

/**
 * GET /api/mission/data - static templates. Optional ?day=N for one day; no day = all.
 * Returns { missionData } or { missionDataList }.
 * Each quest `objectives[]` entry may include `help` and `url` (see MissionDataObjective).
 */
export const getMissionDataApi = async (params?: { day?: number }) => {
  const response = await axios.get('/mission/data', { params: params ?? {} })
  const data = response.data?.data ?? response.data
  return {
    missionData: data?.missionData ?? null,
    missionDataList: data?.missionDataList ?? null,
  }
}

/** POST /api/mission/claim - claim rewards for a quest. Body: { missionDay, questId }. Auth required. */
export const claimMissionRewardsApi = async (missionDay: number, questId: number) => {
  const response = await axios.post('/mission/claim', { missionDay, questId })
  return response.data?.data ?? response.data
}

/** POST /api/mission/mission/complete - mark mission (day) as completed. Body: { missionDay }. Auth required. Mirrors /mission/quest/complete. */
export const completeMissionApi = async (missionDay: number) => {
  const response = await axios.post('/mission/mission/complete', { missionDay })
  return response.data?.data ?? response.data
}

/** POST /api/mission/video-seen - mark mission video as seen. Body: { missionDay }. Auth required. */
export const missionVideoSeenApi = async (missionDay: number) => {
  const response = await axios.post('/mission/video-seen', { missionDay })
  return response.data?.data ?? response.data
}

/** POST /api/mission/objective/complete - mark objective completed. Body: { missionDay, questId, objectiveId }. Auth required. */
export const completeMissionObjectiveApi = async (
  missionDay: number,
  questId: number,
  objectiveId: number,
) => {
  const response = await axios.post('/mission/objective/complete', {
    missionDay,
    questId,
    objectiveId,
  })
  return response.data?.data ?? response.data
}

/**
 * POST /api/mission/objective/visit - record that the user opened a link objective's URL.
 * Raises that objective's count to total so the normal completion flow can complete it.
 * Body: { missionDay, questId, objectiveId }. Auth required. Only valid for objectives with a url.
 */
export const markMissionObjectiveVisitedApi = async (
  missionDay: number,
  questId: number,
  objectiveId: number,
) => {
  const response = await axios.post('/mission/objective/visit', {
    missionDay,
    questId,
    objectiveId,
  })
  return response.data?.data ?? response.data
}

/** POST /api/mission/quest/complete - mark quest completed. Body: { missionDay, questId }. Auth required. */
export const completeMissionQuestApi = async (missionDay: number, questId: number) => {
  const response = await axios.post('/mission/quest/complete', {
    missionDay,
    questId,
  })
  return response.data?.data ?? response.data
}

export const getRepairRequirements = async (walletAddress: string, building: string) => {
  const response = await axios.post('/land/buiding/repair-requirements', {
    walletAddress,
    building,
  })
  return response.data.repairRequired
}

export const repairBuilding = async (walletAddress: string, building: string, material: string) => {
  const response = await axios.post('/land/buiding/repair', {
    walletAddress,
    building,
    material,
  })
  return response.data
}

export const instantRepairBuilding = async (
  walletAddress: string,
  building: string,
  nativeCastle?: number,
) => {
  if (nativeCastle && nativeCastle > 0) {
    const response = await axios.post('/land/castle/instant-repair-building', {
      walletAddress,
      castleId: nativeCastle,
      building,
    })
    return response.data
  } else {
    const response = await axios.post('/land/buiding/instant-repair', {
      walletAddress,
      building,
    })
    return response.data
  }
}

export const setSupportCharacter = async (walletAddress: string, character: string) => {
  const response = await axios.post('/land/buiding/support/set', {
    walletAddress,
    characters: [character],
  })
  return response.data.supportCharacters
}

export const fetchSupportCharacters = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/support/fetch', {
    walletAddress,
    castleId,
  })
  return response.data.characters
}

export const startSupport = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/support/start', {
    walletAddress,
    castleId,
  })
  return response.data.supportState
}

export const stopSupport = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/support/stop', {
    walletAddress,
    castleId,
  })
  return response.data.supportState
}

export const checkBuildingUpgradeable = async (walletAddress: string, building: string) => {
  const response = await axios.post('/land/buiding/upgrade/check', {
    walletAddress,
    building,
  })
  return response.data.upgradeable
}

export const fetchUpgradeRequirements = async (walletAddress: string, building: string) => {
  const response = await axios.post('/land/buiding/upgrade/requirements', {
    walletAddress,
    building,
  })
  return response.data.requirements
}

export const upgradeBuilding = async (
  walletAddress: string,
  building: string,
  upgradeType: 'ecs' | 'resources' = 'resources',
) => {
  const response = await axios.post('/land/buiding/upgrade', {
    walletAddress,
    building,
    upgradeType,
  })
  return response.data
}

export const instantUpgradeBuilding = async (
  walletAddress: string,
  building: string,
  nativeCastle?: number,
) => {
  if (nativeCastle && nativeCastle > 0) {
    const response = await axios.post('/land/castle/instant-upgrade-building', {
      walletAddress,
      castleId: nativeCastle,
      building,
    })
    return response.data
  } else {
    const response = await axios.post('/land/buiding/instant-upgrade', {
      walletAddress,
      building,
    })
    return response.data
  }
}

export const removeCharacterFromBuilding = async (
  walletAddress: string,
  building: string,
  character: string,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/remove/character', {
    walletAddress,
    building,
    characterId: character,
    castleId,
  })
  return response.data
}

export const addCharacterToBuilding = async (
  walletAddress: string,
  building: string,
  character: string,
  order: number,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/add/character', {
    walletAddress,
    building,
    characterId: character,
    order,
    castleId,
  })
  return response.data.character
}

export const startWell = async (
  walletAddress: string,
  positionIndex: number,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/well/start', {
    walletAddress,
    positionIndex,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkWell = async (
  walletAddress: string,
  positionIndex: number,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/well/check', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const claimWell = async (
  walletAddress: string,
  positionIndex: number,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/well/claim', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const cancelWell = async (
  walletAddress: string,
  positionIndex: number,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/well/cancel', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const immediateWell = async (
  walletAddress: string,
  positionIndex: number,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/well/immediate', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

// Tower
export const startTowerMining = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/tower/start', {
    walletAddress,
  })
  return response.data
}

export const stopTowerMining = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/tower/stop', {
    walletAddress,
  })
  return response.data
}

export const checkTowerMining = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/tower/check', {
    walletAddress,
  })
  return response.data
}

export const claimTowerMining = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/tower/claim', {
    walletAddress,
  })
  return response.data
}

export const cancelTower = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/tower/cancel', {
    walletAddress,
  })
  return response.data
}

// Training
export const startTraining = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/training/start', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const checkTraining = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/training/check', {
    walletAddress,
    castleId,
  })
  return response.data
}

// ✨ Mining Factory Payment API
export interface CreatePaymentIntentParams {
  walletAddress: string
  factoryType: number
  chain?: string
}

export interface PaymentIntentResponse {
  success: boolean
  intentId: string
  address: string
  chain: string
  tokenContract: string
  expectedAmount: string
  expiresAt: string
  qrURI: string
  walletDeepLink: string
  requiredConfirmations: number
  websocketChannel?: string
  message?: string
}

export interface PaymentStatusResponse {
  success: boolean
  intentId?: string
  status: string
  expectedAmount: string
  receivedAmount: string
  address?: string
  chain?: string
  confirmations: number
  requiredConfirmations: number
  txHash?: string
  expiresAt: string
  deliveredAt?: string
  factoryType?: number
  factoryLevel?: number
  message?: string
}

export interface MiningFactory {
  _id: string
  walletAddress: string
  factoryType: number
  level: number
  hp: {
    current: number
    max: number
  }
  assignedCharacters: Array<{
    characterInstanceId: string
    assignedAt: string
    position: number
  }>
  miningGem?: {
    gemId: string
    gemType: string
    gemBonus: number
  }
  isMining: boolean
  miningStartedAt?: string
  lastMiningUpdate?: string
  totalMined: number
  createdAt: string
}

export interface AssignedCharacter {
  characterInstanceId: {
    _id: string
    abilities: {
      armor: number
      attack: number
      ecs: number
      health: number
      usdt: number
      water: number
    }
    characterId: string
    characterIdle: string
    characterImg: string
    characterName: string
    createdAt: string
    experience: number
    isUsing: boolean
    lastUsedAt: string
    level: number
    rarity: 'common' | 'rare' | 'legendary'
    stats: any
    updatedAt: string
    walletAddress: string
    __v: number
  }
  assignedAt: string
  position: number
}

export interface MiningFactoryDetails {
  _id: string
  walletAddress: string
  factoryType: number
  level: number
  hp: {
    current: number
    max: number
  }
  assignedCharacters: Array<AssignedCharacter>
  miningGem?: {
    gemId: string
    gemType: string
    gemBonus: number
  }
  isMining: boolean
  miningStartedAt?: string
  lastMiningUpdate?: string
  totalMined: number
  pendingMinedUsdt: number
  lastMiningDate: string
  createdAt: string
  updatedAt: string
  dailyMiningRateUsdt: number
}

export interface FactoryPriceResponse {
  success: boolean
  factoryType: number
  level: number
  priceUSDT: number
  message?: string
}

export interface PurchaseFactoryResponse {
  success: boolean
  factory: MiningFactory
  message?: string
}

// Create payment intent for factory purchase (USDT)
export const createFactoryPaymentIntent = async (
  params: CreatePaymentIntentParams,
): Promise<PaymentIntentResponse> => {
  const response = await axios.post('/mining-factory/payment-intent', {
    walletAddress: params.walletAddress,
    factoryType: params.factoryType,
    // factoryLevel is optional - backend auto-calculates based on existing factory
    chain: params.chain || 'TRC20',
  })
  return response.data
}

export const claimTraining = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/training/claim', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const immediateTraining = async (
  walletAddress: string,
  characterId: string,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/training/immediate', {
    walletAddress,
    characterId,
    castleId,
  })
  return response.data
}

// Get payment intent status
export const getPaymentIntentStatus = async (
  intentId: string,
  walletAddress: string,
): Promise<PaymentStatusResponse> => {
  const response = await axios.get(`/mining-factory/payment-intent/${intentId}`, {
    params: { walletAddress },
  })
  return response.data
}

// Hospital
export const healCharacter = async (
  walletAddress: string,
  characterId: string,
  paymentMethod: 'bread' = 'bread',
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/hospital/heal', {
    walletAddress,
    characterId,
    paymentMethod,
    castleId,
  })
  return response.data
}

/** FTUE phase 2: record that a one-time feature explanation was shown (server-side, idempotent). */
export const markExplanationSeenApi = async (walletAddress: string, key: string) => {
  const response = await axios.post('/user/explanation/seen', { walletAddress, key })
  return response.data
}

/**
 * Day-1 heal quest: make sure somebody actually needs healing before the
 * hospital opens. The server re-derives the quest state, so this is a no-op
 * outside that one step. Returns `{ wounded }` — refetch characters if true.
 */
export const ensureHealQuestPatientApi = async (walletAddress: string) => {
  const response = await axios.post('/mission/heal-quest/ensure-patient', { walletAddress })
  return response.data
}

// Get factory price (USDT)
export const getFactoryPrice = async (
  factoryType: number,
  level: number,
): Promise<FactoryPriceResponse> => {
  const response = await axios.get(`/mining-factory/price/${factoryType}/${level}`)
  return response.data
}

// Get user's factories
export const getUserFactories = async (
  walletAddress: string,
): Promise<{ success: boolean; factories: MiningFactory[]; message?: string }> => {
  const response = await axios.get('/mining-factory/factories', {
    params: { walletAddress },
  })
  return response.data
}

// Get user's weather forecast status
export const getUserWeatherForecast = async (
  walletAddress: string,
): Promise<{
  success: boolean
  unlocked?: boolean
  level?: number
  hp?: { current: number; max: number }
  message?: string
  _id?: string
  walletAddress?: string
  createdAt?: string
  updatedAt?: string
  assignedCharacter?: {
    characterInstanceId?: AssignedCharacter['characterInstanceId']
    assignedAt?: string
  }
  nextDisaster?: {
    type: string
    scheduledTime: string
    damageAmount: number
  }
  equippedGems?: Array<{
    gemType: string
    itemId: string
    equippedAt: string
  }>
  detectedWeather?: string
}> => {
  try {
    const response = await axios.get('/mining-factory/weather-forecast/user-weather-forecast', {
      params: { walletAddress },
    })
    // Backend returns weatherForecast object if found
    if (response.data?.weatherForecast) {
      return {
        success: true,
        unlocked: true,
        level: response.data.weatherForecast.level || 0,
        hp: response.data.weatherForecast.hp || { current: 0, max: 1000 },
        _id: response.data.weatherForecast._id,
        walletAddress: response.data.weatherForecast.walletAddress,
        createdAt: response.data.weatherForecast.createdAt,
        updatedAt: response.data.weatherForecast.updatedAt,
        assignedCharacter: response.data.weatherForecast.assignedCharacter || null,
        equippedGems: response.data.weatherForecast.equippedGems || [],
        detectedWeather: response.data.weatherForecast.detectedWeather || '',
        nextDisaster: response.data.weatherForecast.nextDisaster || undefined,
      }
    }
    return { success: false, unlocked: false, level: 0 }
  } catch (error: any) {
    // 404 means weather forecast doesn't exist (level 0)
    if (error?.response?.status === 404) {
      return { success: true, unlocked: false, level: 0 }
    }
    return { success: false, unlocked: false, level: 0, message: error?.message }
  }
}

// Get weather forecast price for a level
export const getWeatherForecastPrice = async (
  level: number,
): Promise<{ success: boolean; level: number; priceUSDT: number; message?: string }> => {
  const response = await axios.get(`/mining-factory/weather-forecast/price/${level}`)
  return response.data
}

// Purchase or upgrade weather forecast via USDT
export const purchaseWeatherForecastUSDT = async (
  walletAddress: string,
  txID: string,
): Promise<{ success: boolean; weatherForecast?: any; priceUSDT?: number; message?: string }> => {
  const response = await axios.post('/mining-factory/weather-forecast/buy', {
    walletAddress,
    txID,
  })
  return response.data
}

export const claimHospital = async (walletAddress: string) => {
  const response = await axios.post('/land/buiding/hospital/claim', {
    walletAddress,
  })
  return response.data
}

// Get specific factory by ID
export const getFactoryById = async (
  factoryId: string,
  walletAddress: string,
): Promise<{ success: boolean; factory: MiningFactoryDetails; message?: string }> => {
  const response = await axios.get(`/mining-factory/${factoryId}`, {
    params: { walletAddress },
  })
  return response.data
}

// Buy or upgrade factory via Solana USDT (frontend sends token; backend verifies signature)
export const purchaseFactoryUSDT = async (
  walletAddress: string,
  factoryType: number,
  txID: string,
): Promise<{ success: boolean; factory: MiningFactory; message?: string }> => {
  const response = await axios.post('/mining-factory/buy', {
    walletAddress,
    factoryType,
    txID,
  })
  return response.data
}

export const swapWater = async (walletAddress: string, water: number) => {
  const response = await axios.post('/land/buiding/swap/water', {
    walletAddress,
    water,
  })
  return response.data
}

export const swapWheat = async (walletAddress: string, wheat: number) => {
  const response = await axios.post('/land/buiding/swap/wheat', {
    walletAddress,
    wheat,
  })
  return response.data
}

export const swapBread = async (walletAddress: string, bread: number) => {
  const response = await axios.post('/land/buiding/swap/bread', {
    walletAddress,
    bread,
  })
  return response.data
}

export const swapWood = async (walletAddress: string, wood: number) => {
  const response = await axios.post('/land/buiding/swap/wood', {
    walletAddress,
    wood,
  })
  return response.data
}

export const swapStone = async (walletAddress: string, stone: number) => {
  const response = await axios.post('/land/buiding/swap/stone', {
    walletAddress,
    stone,
  })
  return response.data
}

export const swapIron = async (walletAddress: string, iron: number) => {
  const response = await axios.post('/land/buiding/swap/iron', {
    walletAddress,
    iron,
  })
  return response.data
}

export interface MarketTradePayload {
  sell: { wood: number; stone: number; iron: number; bread: number }
  buy: { wood: number; stone: number; iron: number; bread: number }
}

export const marketTrade = async (
  walletAddress: string,
  payload: MarketTradePayload,
): Promise<{
  success: boolean
  ecs?: number
  water?: number
  iron?: number
  wood?: number
  stone?: number
  bread?: number
  wheat?: number
  message?: string
}> => {
  const response = await axios.post('/land/buiding/market/trade', {
    walletAddress,
    sell: payload.sell,
    buy: payload.buy,
  })
  return response.data
}

export const checkCooldownHunting = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/hunting/check', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const startHunting = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/hunting/start', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const claimHunting = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/hunting/claim', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const cancelHunting = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/hunting/cancel', {
    walletAddress,
    castleId,
  })
  return response.data
}

// Bakery
export const startBakery = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/bakery/start', {
    walletAddress,
    positionIndex,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkBakeryCooldown = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/bakery/check', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const claimBakeryReward = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/bakery/claim', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const cancelBakery = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/bakery/cancel', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const immediateBakery = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/bakery/immediate', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

// Wheat Farm
export const startWheatFarm = async (
  walletAddress: string,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/wheat-farm/start', {
    walletAddress,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkWheatFarmCooldown = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wheat-farm/check', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const claimWheatFarmReward = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wheat-farm/claim', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const cancelWheatFarm = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wheat-farm/cancel', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const immediateWheatFarm = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wheat-farm/immediate', {
    walletAddress,
    castleId,
  })
  return response.data
}

// Wood House
export const startWoodHouse = async (
  walletAddress: string,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/wood-house/start', {
    walletAddress,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkWoodHouseCooldown = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wood-house/check', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const claimWoodHouseReward = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wood-house/claim', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const cancelWoodHouse = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wood-house/cancel', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const immediateWoodHouse = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/wood-house/immediate', {
    walletAddress,
    castleId,
  })
  return response.data
}

// Stone Factory
export const startStoneFactory = async (
  walletAddress: string,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/stone-factory/start', {
    walletAddress,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkStoneFactoryCooldown = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/stone-factory/check', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const claimStoneFactoryReward = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/stone-factory/claim', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const cancelStoneFactory = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/stone-factory/cancel', {
    walletAddress,
    castleId,
  })
  return response.data
}

export const immediateStoneFactory = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/buiding/stone-factory/immediate', {
    walletAddress,
    castleId,
  })
  return response.data
}

// Iron Mine (positionIndex 1 = Iron Mine 1, 2 = Iron Mine 2)
export const startIronMine = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
  orderDurationSec?: number,
) => {
  const response = await axios.post('/land/buiding/iron-mine/start', {
    walletAddress,
    positionIndex,
    castleId,
    orderDurationSec,
  })
  return response.data
}

export const checkIronMineCooldown = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/iron-mine/check', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const claimIronMineReward = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/iron-mine/claim', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const cancelIronMine = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/iron-mine/cancel', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

export const immediateIronMine = async (
  walletAddress: string,
  positionIndex: number = 1,
  castleId?: number,
) => {
  const response = await axios.post('/land/buiding/iron-mine/immediate', {
    walletAddress,
    positionIndex,
    castleId,
  })
  return response.data
}

// Blacksmith
export const startBlacksmithCraft = async (
  walletAddress: string,
  weaponId: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/start', {
    walletAddress,
    weaponId,
    castleId,
    positionIndex,
  })
  return response.data
}

export const checkBlacksmithCraft = async (
  walletAddress: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/check', {
    walletAddress,
    castleId,
    positionIndex,
  })
  return response.data
}

export const claimBlacksmithCraft = async (
  walletAddress: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/claim', {
    walletAddress,
    castleId,
    positionIndex,
  })
  return response.data
}

export const cancelBlacksmithCraft = async (
  walletAddress: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/cancel', {
    walletAddress,
    castleId,
    positionIndex,
  })
  return response.data
}

export const immediateBlacksmithCraft = async (
  walletAddress: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/immediate', {
    walletAddress,
    castleId,
    positionIndex,
  })
  return response.data
}

export const getBlacksmithRecipes = async (
  walletAddress: string,
  castleId?: number,
  positionIndex?: number,
) => {
  const response = await axios.post('/land/buiding/blacksmith/recipes', {
    walletAddress,
    castleId,
    positionIndex,
  })
  return response.data
}

export const checkAllMiningStatus = async (walletAddress: string, castleId?: number) => {
  const cid = castleId === undefined || castleId === null ? 'all' : String(castleId)
  return dedupeInFlight(`land:mining:check-all:${walletAddress}:${cid}`, async () => {
    const response = await axios.post('/land/buiding/mining/check-all', {
      walletAddress,
      castleId,
    })
    return response.data
  })
}

// Legacy: Purchase factory with ECS tokens (backward compatibility)
export const purchaseFactoryWithECS = async (
  walletAddress: string,
  factoryType: number,
): Promise<PurchaseFactoryResponse> => {
  const response = await axios.post('/mining-factory/purchase', {
    walletAddress,
    factoryType,
  })
  return response.data
}

// Start mining for a factory
export const startMining = async (
  walletAddress: string,
  factoryType: number,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/start-mining', {
    walletAddress,
    factoryType,
  })
  return response.data
}

// Claim mined USDT from factory
export const claimMinedUsdt = async (
  walletAddress: string,
  factoryType: number,
): Promise<{ success: boolean; claimedAmount?: number; message?: string }> => {
  const response = await axios.post('/mining-factory/claim-sol', {
    walletAddress,
    factoryType,
  })
  return response.data
}

// Assign character to factory
export const assignCharacterToFactory = async (
  walletAddress: string,
  factoryType: number,
  characterInstanceId: string,
  position: number,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/assign-character', {
    walletAddress,
    factoryType,
    characterInstanceId,
    position,
  })
  return response.data
}

// Remove character from factory
export const removeCharacterFromFactory = async (
  walletAddress: string,
  factoryType: number,
  characterInstanceId: string,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/remove-character', {
    walletAddress,
    factoryType,
    characterInstanceId,
  })
  return response.data
}

// Assign gem to factory
export const assignGemToFactory = async (
  walletAddress: string,
  factoryType: number,
  gemType: string,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/assign-gem', {
    walletAddress,
    factoryType,
    gemType,
  })
  return response.data
}

// Remove gem from factory
export const removeGemFromFactory = async (
  walletAddress: string,
  factoryType: number,
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/remove-gem', {
    walletAddress,
    factoryType,
  })
  return response.data
}

// Restore/repair factory
export const restoreFactory = async (
  walletAddress: string,
  factoryType: number,
  restoreMethod: 'gem' | 'ecs',
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/restore-factory', {
    walletAddress,
    factoryType,
    restoreMethod,
  })
  return response.data
}

// Repair weather forecast
export const repairWeatherForecast = async (
  walletAddress: string,
  restoreMethod: 'gem' | 'ecs',
): Promise<{ success: boolean; message?: string }> => {
  const response = await axios.post('/mining-factory/weather-forecast/repair', {
    walletAddress,
    restoreMethod,
  })
  return response.data
}

// Predict next disaster
export const predictNextDisaster = async (
  walletAddress: string,
): Promise<{
  success: boolean
  nextDisaster?: {
    type: string
    scheduledTime: string
    damageAmount: number
  }
  canDetect?: boolean
  predictedWeather?: string
  message?: string
}> => {
  const response = await axios.post('/mining-factory/weather-forecast/predict-next-disaster', {
    walletAddress,
  })
  return response.data
}

// Person Bonus API
export const checkPersonBonus = async (
  walletAddress: string,
): Promise<{
  success: boolean
  hasBonus: boolean
  objectId?: 'person1' | 'person2' | 'person3' | 'person4'
  message?: string
}> => {
  const response = await axios.post('/land/person-bonus/check', {
    walletAddress,
  })
  return response.data
}

export const claimPersonBonus = async (
  walletAddress: string,
  objectId: 'person1' | 'person2' | 'person3' | 'person4',
): Promise<{
  success: boolean
  claimedAmount?: number
  newBalance?: number
  message?: string
}> => {
  const response = await axios.post('/land/person-bonus/claim', {
    walletAddress,
    objectId,
  })
  return response.data
}

// Daily Reward API
export interface DailyReward {
  date: string
  activeToday: boolean
  rewardsTokens: number
  status: 'skipped' | 'computed' | 'sent' | 'failed'
  txSignature: string
  failReason?: string
  totalTokens: number
  earnedTokens: number
  spentTokens: number
}

export interface LoginStreakPayload {
  streakDay: number
  claimedToday: boolean
  today: string
  consecutiveStreak?: number
  rewardTier?: number
  lastClaimedSlot?: string | null
}

export interface DailyRewardsResponse {
  success: boolean
  rewards: { [date: string]: DailyReward }
  pendingTotalUsdt?: number
  pendingTotalSol?: number
  loginStreak?: LoginStreakPayload | null
  today: string
  message?: string
}

export const getDailyRewards = async (walletAddress: string): Promise<DailyRewardsResponse> => {
  const response = await axios.post('/daily-reward/get', {
    walletAddress,
  })
  return response.data
}

export const claimDailyReward = async (
  walletAddress: string,
  date?: string,
): Promise<{
  success: boolean
  message?: string
  txSignature?: string
  amount?: number
  pendingTotalUsdt?: number
}> => {
  const response = await axios.post('/daily-reward/claim', {
    walletAddress,
    date,
  })
  return response.data
}

export const claimLoginStreakDailyReward = async (
  walletAddress: string,
): Promise<{
  success: boolean
  message?: string
  rewardTier?: number
  consecutiveStreak?: number
  today?: string
  wheat?: number
  bread?: number
  wood?: number
  stone?: number
  iron?: number
  water?: number
  /** Day-7 tier grants ECS gems; updated balance returned for sync. */
  ecsTokenAmount?: number
  payoutCount?: number
  payoutTiers?: number[]
  claimedToday?: boolean
  _httpStatus?: number
}> => {
  const response = await axios.post(
    '/daily-reward/login-streak/claim',
    { walletAddress },
    { validateStatus: (status) => status === 200 || status === 400 || status === 404 },
  )
  return { ...response.data, _httpStatus: response.status }
}

// ── Random reward board ───────────────────────────────────
/** The single card claimed in the current cooldown window. */
export interface RandomRewardCardPrize {
  slot: number
  prizeId: string
  amount: number
}

export interface RandomRewardBoardResponse {
  success: boolean
  message?: string
  slots: number
  cooldownMs: number
  /** Face-up card for this window, or null while a draw is available. */
  card: RandomRewardCardPrize | null
  /** Epoch ms when the next draw unlocks; 0 if the player has never claimed. */
  nextDrawAt: number
  canDraw: boolean
  /**
   * The server's clock when it answered. The cooldown runs from the claim rather than from a
   * calendar boundary, so remaining time is measured against this, not against the device clock.
   */
  serverNow: number
}

export const getRandomRewardBoard = async (
  walletAddress: string,
): Promise<RandomRewardBoardResponse> => {
  const response = await axios.post('/random-reward/get', { walletAddress })
  return response.data
}

export type RandomRewardClaimResponse = RandomRewardBoardResponse & {
  /** Present only on success — what the card turned out to hold. */
  prize?: RandomRewardCardPrize
  /**
   * Every slot's contents, so the reveal can turn the whole board face-up and show what was passed
   * over. Only `prize` is actually paid; the rest are the server's, not invented here.
   */
  layout?: RandomRewardCardPrize[]
  wheat?: number
  bread?: number
  wood?: number
  stone?: number
  iron?: number
  water?: number
  ecsTokenAmount?: number
  _httpStatus?: number
}

/**
 * Picks a card: rolled, paid and the cooldown started, in one call. Picking is claiming, so a
 * player who closes the modal the instant the card turns over has still been paid.
 */
export const claimRandomReward = async (
  walletAddress: string,
  slot: number,
): Promise<RandomRewardClaimResponse> => {
  const response = await axios.post(
    '/random-reward/claim',
    { walletAddress, slot },
    { validateStatus: (status) => status === 200 || status === 400 || status === 404 },
  )
  return { ...response.data, _httpStatus: response.status }
}

// ── Account level-up rewards ────────────────────────────────────────────────────────
/**
 * One reward line. Randomised rewards (character / weapon / basicItem) carry only their
 * shape before a claim; `key`/`name`/`image` are filled in by the claim response once the
 * server has actually rolled them.
 */
export type LevelRewardLine = {
  type:
    | 'resource'
    | 'basicItem'
    | 'item'
    | 'character'
    | 'worker'
    | 'weapon'
    | 'characterCompensation'
  key?: string
  name?: string
  image?: string
  amount: number
  rarity?: 'common' | 'rare' | 'legendary'
  pool?: 'parent' | 'child'
  tiers?: number[]
}

export type LevelRewardBundle = { level: number; rewards: LevelRewardLine[] }

/** Levels reached but not yet claimed, oldest first. */
export const getUserLevelRewards = async (
  walletAddress: string,
): Promise<{
  userLvl?: number
  claimable?: LevelRewardBundle[]
  nextLevel?: LevelRewardBundle | null
  claimedLevels?: number[]
}> => {
  // RESPONSE() spreads the payload at the top level of the body — there is no `.data` envelope.
  const response = await axios.post('/user/level-rewards', { walletAddress })
  return response.data ?? {}
}

/** Claim one level. Idempotent server-side — a repeat returns 400 with `alreadyClaimed`. */
export const claimUserLevelReward = async (
  walletAddress: string,
  level: number,
): Promise<{
  success?: boolean
  message?: string
  level?: number
  granted?: LevelRewardLine[]
  alreadyClaimed?: boolean
  _httpStatus?: number
}> => {
  const response = await axios.post(
    '/user/level-rewards/claim',
    { walletAddress, level },
    { validateStatus: (status) => status === 200 || status === 400 || status === 404 },
  )
  return { ...(response.data ?? {}), _httpStatus: response.status }
}

// ECS Pack Purchase API
export const getECSPrice = async (): Promise<{
  price: number
  source: string
}> => {
  const response = await axios.get('/user/ecs-pack/price')
  return response.data
}

export const purchaseECSPack = async (
  walletAddress: string,
  packSize: number,
  txID: string,
): Promise<{
  success: boolean
  ecsTokenAmount?: number
  packSize?: number
  baseECS?: number
  bonusECS?: number
  totalECS?: number
  usdtCost?: number
  message?: string
}> => {
  const response = await axios.post('/user/ecs-pack/purchase', {
    walletAddress,
    packSize,
    txID,
  })
  return response.data
}

// Referral API
export interface ReferralEarningRow {
  /** 1 = someone you invited directly. 2 = someone they invited. */
  level: number
  ratePercent: number
  buyerName: string
  buyerWallet: string
  purchaseKind: 'gem_pack' | 'membership'
  packSize: number
  usdtPaid: number
  /** Total gems the buyer received — what the commission was calculated from. */
  basisEcs: number
  /** Gems this row is worth to you. */
  amountEcs: number
  /** 'claimable' = pending, waiting on the Claim button. 'claimed' = already banked. */
  status: 'claimable' | 'claimed'
  createdAt: string
}

export interface ReferralEarnings {
  success: boolean
  refCode: string
  inviteCount: number
  /** Server-advertised commission rates, so the client never hardcodes 10 / 3. */
  rates: { level: number; ratePercent: number }[]
  /** Pending gems — what the Claim button banks, and what drives the tab's red dot. */
  claimableEcs: number
  /** Gems already banked. */
  claimedEcs: number
  /** Lifetime earned, claimed or not. */
  totalEcs: number
  byLevel: { level: number; ratePercent: number; totalEcs: number; count: number }[]
  count: number
  rows: ReferralEarningRow[]
}

export const getReferralEarnings = async (
  walletAddress: string,
  opts: { limit?: number; skip?: number } = {},
): Promise<ReferralEarnings> => {
  const response = await axios.post('/user/userLP/earnings', {
    walletAddress,
    limit: opts.limit ?? 25,
    skip: opts.skip ?? 0,
  })
  return response.data
}

/** Banks every pending referral bonus into the gem balance. */
export const claimReferralBonus = async (
  walletAddress: string,
): Promise<{
  success: boolean
  claimedEcs?: number
  rowCount?: number
  ecsTokenAmount?: number
  message?: string
}> => {
  const response = await axios.post('/user/userLP/claim-bonus', { walletAddress })
  return response.data
}

// PvP API
export interface PvPRoom {
  roomId: string
  creatorAddress: string
  creatorNickname?: string
  creatorLevel?: number
  creatorExp?: number
  creatorPvpMmr?: number
  creatorEcs?: number
  creatorUsdt?: number
  creatorAvatar?: string | null
  creatorCharacters?: any[] // Array of 1-3 characters
  creatorCharacter?: any // Legacy single character (deprecated)
  joinerAddress?: string
  joinerNickname?: string
  joinerLevel?: number
  joinerExp?: number
  joinerPvpMmr?: number
  joinerEcs?: number
  joinerUsdt?: number
  joinerAvatar?: string | null
  joinerCharacters?: any[] // Array of 1-3 characters
  joinerCharacter?: any // Legacy single character (deprecated)
  entryFee: number
  roomType?: 'free' | 'usdt' | 'ecs'
  characterCount?: number // Number of characters per player (1, 2, or 3), default 3
  canJoin?: boolean
  status:
    | 'waiting'
    | 'character_selection'
    | 'ready'
    | 'payment_pending'
    | 'payment_completed'
    | 'in_battle'
    | 'battle_started'
    | 'cancelled'
    | 'completed'
  isCreator?: boolean
  isJoiner?: boolean
  createdAt?: string
  battleId?: string
  /** Matchmaking/tournament ranked queue — rematch disabled in battle end UI. */
  isTournament?: boolean
}

// Avatar APIs
export interface Avatar {
  id: string
  name?: string // Optional - not required from backend
  price: number
  isPurchased: boolean
  isCurrent: boolean
  imageUrl: string
}

export interface AvatarsListResponse {
  success: boolean
  avatars: Avatar[]
  currentAvatar: string | null | { id: string | null; imageUrl: string | null }
  message?: string
}

export const getAvailableAvatars = async (walletAddress: string): Promise<AvatarsListResponse> => {
  const response = await axios.post('/user/avatars/list', {
    walletAddress,
  })
  return response.data
}

export const checkActiveBattle = async (
  walletAddress: string,
): Promise<{
  success: boolean
  hasActiveBattle: boolean
  battleId?: string
  roomId?: string
  redirectTo?: 'battle'
  error?: string
}> => {
  try {
    // Polled every ~2.5s by the matchmaking waiting page to open the arena the moment a match
    // (bot or human) is created. It MUST bypass every cache layer — a stale "no active battle"
    // (browser/proxy 304 or a short CDN TTL) would keep the page stuck on "searching" while the
    // battle already exists. `_ts` makes each request a unique URL; the headers cover the rest.
    const response = await axios.get(`/pvp/check-active-battle`, {
      params: { walletAddress, _ts: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    return {
      success: true,
      hasActiveBattle: response.data.hasActiveBattle || false,
      battleId: response.data.battleId,
      roomId: response.data.roomId,
      redirectTo: response.data.redirectTo,
    }
  } catch (error: any) {
    console.error('Error checking active battle:', error)
    return {
      success: false,
      hasActiveBattle: false,
      error: error.response?.data?.error || 'Failed to check active battle',
    }
  }
}

function mapApiRowToPvpBattleFinishedLogEntry(
  row: Record<string, unknown>,
): PvpBattleFinishedLogEntry {
  return {
    id: String(row.id),
    battleId: String(row.battleId),
    roomId: String(row.roomId ?? ''),
    player1: String(row.player1 ?? ''),
    player1Characters: Array.isArray(row.player1Characters) ? row.player1Characters : [],
    player2: String(row.player2 ?? ''),
    player2Characters: Array.isArray(row.player2Characters) ? row.player2Characters : [],
    player1Level: (() => {
      const v = row.player1Level
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })(),
    player2Level: (() => {
      const v = row.player2Level
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })(),
    player1Nickname: (() => {
      const v = row.player1Nickname
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    player2Nickname: (() => {
      const v = row.player2Nickname
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    player1PvpMmr: (() => {
      const v = row.player1PvpMmr
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })(),
    player2PvpMmr: (() => {
      const v = row.player2PvpMmr
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })(),
    player1AvatarUrl: (() => {
      const v = row.player1AvatarUrl
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    player2AvatarUrl: (() => {
      const v = row.player2AvatarUrl
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    stakeType:
      row.stakeType === 'ecs' || row.stakeType === 'usdt' || row.stakeType === 'free'
        ? row.stakeType
        : 'free',
    stakeAmount: Number(row.stakeAmount ?? 0),
    winnerWallet: row.winnerWallet != null ? String(row.winnerWallet) : null,
    battleEndedAt:
      row.battleEndedAt != null
        ? typeof row.battleEndedAt === 'string'
          ? row.battleEndedAt
          : new Date(row.battleEndedAt as string | number | Date).toISOString()
        : '',
    endReason: String(row.endReason ?? 'none'),
  }
}

export const postPvpHonorRewardClaim = async (
  walletAddress: string,
  tier: string,
  kind: PvpHonorRewardKind,
): Promise<{
  success: boolean
  data?: { state: PvpHonorClaimsRow; user?: Record<string, unknown>; applied?: unknown[] }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/honor-rewards/claim', {
      walletAddress,
      tier: String(tier),
      kind: String(kind),
    })
    const d = response.data?.data
    if (response.data?.success && d?.state) {
      return {
        success: true,
        data: {
          state: d.state as PvpHonorClaimsRow,
          user: d.user as Record<string, unknown> | undefined,
          applied: Array.isArray(d.applied) ? (d.applied as unknown[]) : undefined,
        },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error claiming PvP honor reward:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Claim failed',
    }
  }
}

function mapApiTournamentActivityItem(
  row: Record<string, unknown>,
): TournamentHubActivityItem | null {
  const kind = row.kind === 'join' ? 'join' : row.kind === 'battle' ? 'battle' : null
  if (kind === 'join') {
    const pid = row.participantId != null ? String(row.participantId).trim() : ''
    if (!pid) return null
    const joinedAt =
      row.joinedAt != null
        ? typeof row.joinedAt === 'string'
          ? row.joinedAt
          : new Date(row.joinedAt as string | number | Date).toISOString()
        : ''
    if (!joinedAt) return null
    const nl = row.nickname
    const nickname = nl != null && typeof nl === 'string' && nl.trim() ? nl.trim() : null
    const ul = row.userLevel
    const userLevel =
      ul == null || ul === ''
        ? null
        : (() => {
            const n = Number(ul)
            return Number.isFinite(n) ? n : null
          })()
    const mm = row.pvpMmr
    const pvpMmr =
      mm == null || mm === ''
        ? null
        : (() => {
            const n = Number(mm)
            return Number.isFinite(n) ? n : null
          })()
    const au = row.avatarUrl
    const avatarUrl = au != null && typeof au === 'string' && au.trim() ? au.trim() : null

    return {
      kind: 'join',
      participantId: pid,
      walletAddress: String(row.walletAddress ?? ''),
      nickname,
      userLevel,
      pvpMmr,
      avatarUrl,
      joinedAt,
    }
  }
  if (kind === 'battle') {
    const base = mapApiRowToPvpBattleFinishedLogEntry(row)
    return { kind: 'battle', ...base }
  }
  const battleId = row.battleId != null ? String(row.battleId) : ''
  if (!battleId) return null
  const base = mapApiRowToPvpBattleFinishedLogEntry(row)
  return { kind: 'battle', ...base }
}

export const getUserPvPBattleFinishedLogs = async (
  walletAddress: string,
  opts?: { limit?: number; skip?: number },
): Promise<{
  success: boolean
  data?: { logs: PvpBattleFinishedLogEntry[]; total: number; limit: number; skip: number }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/battle-finished-logs', {
      walletAddress,
      limit: opts?.limit,
      skip: opts?.skip,
    })
    const d = response.data?.data
    if (response.data?.success && d && Array.isArray(d.logs)) {
      const logs: PvpBattleFinishedLogEntry[] = d.logs.map((row: Record<string, unknown>) =>
        mapApiRowToPvpBattleFinishedLogEntry(row),
      )
      return {
        success: true,
        data: {
          logs,
          total: Number(d.total ?? 0),
          limit: Number(d.limit ?? 20),
          skip: Number(d.skip ?? 0),
        },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error fetching PvP battle finished logs:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch battle logs',
    }
  }
}

/** Tournament hub: battles + join events in [startsAt, endsAt] (ISO). Pass `tournamentId` when known so joins match the correct event. */
export const fetchTournamentPvPBattleFinishedLogs = async (opts: {
  startsAt: string
  endsAt: string
  tournamentId?: number | null
  limit?: number
  skip?: number
}): Promise<{
  success: boolean
  data?: { items: TournamentHubActivityItem[]; total: number; limit: number; skip: number }
  message?: string
}> => {
  try {
    const tid = opts.tournamentId
    const response = await axios.post('/pvp/tournament-battle-finished-logs', {
      startsAt: opts.startsAt,
      endsAt: opts.endsAt,
      ...(tid != null && tid >= 1 ? { tournamentId: tid } : {}),
      limit: opts.limit,
      skip: opts.skip,
    })
    const d = response.data?.data
    if (response.data?.success && d && Array.isArray(d.items)) {
      const items: TournamentHubActivityItem[] = []
      for (const row of d.items as Record<string, unknown>[]) {
        const mapped = mapApiTournamentActivityItem(row)
        if (mapped) items.push(mapped)
      }
      return {
        success: true,
        data: {
          items,
          total: Number(d.total ?? 0),
          limit: Number(d.limit ?? 20),
          skip: Number(d.skip ?? 0),
        },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error fetching tournament PvP battle logs:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch tournament activity',
    }
  }
}

export function parseRankedFeedEntryFromSocket(data: unknown): PvpRankedBattleFeedEntry | null {
  if (!data || typeof data !== 'object') return null
  return parseRankedFeedEntry(data as Record<string, unknown>)
}

function parseRankedFeedEntry(row: Record<string, unknown>): PvpRankedBattleFeedEntry | null {
  const battleId = row.battleId != null ? String(row.battleId) : ''
  if (!battleId) return null
  const currency = row.currency === 'ecs' || row.currency === 'usdt' ? row.currency : 'usdt'
  const bonus = row.winnerBonusEcs
  const bonusNum = bonus != null ? Number(bonus) : 0
  const cursorId =
    row.cursorId != null && typeof row.cursorId === 'string' ? row.cursorId.trim() : ''
  return {
    ...(cursorId ? { cursorId } : {}),
    battleId,
    roomId: String(row.roomId ?? ''),
    battleEndedAt:
      row.battleEndedAt != null
        ? typeof row.battleEndedAt === 'string'
          ? row.battleEndedAt
          : new Date(row.battleEndedAt as string | number | Date).toISOString()
        : new Date().toISOString(),
    player1: String(row.player1 ?? ''),
    player2: String(row.player2 ?? ''),
    player1Level: (() => {
      const v = row.player1Level
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? Math.min(99, Math.floor(n)) : null
    })(),
    player2Level: (() => {
      const v = row.player2Level
      if (v == null || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? Math.min(99, Math.floor(n)) : null
    })(),
    player1Nickname:
      row.player1Nickname != null && typeof row.player1Nickname === 'string'
        ? row.player1Nickname.trim() || null
        : null,
    player2Nickname:
      row.player2Nickname != null && typeof row.player2Nickname === 'string'
        ? row.player2Nickname.trim() || null
        : null,
    winnerWallet: row.winnerWallet != null ? String(row.winnerWallet) : null,
    isTie: Boolean(row.isTie),
    currency,
    stakeAmount: Number(row.stakeAmount ?? 0),
    winnerAmount: Number(row.winnerAmount ?? 0),
    loserAmount: Number(row.loserAmount ?? 0),
    ...(Number.isFinite(bonusNum) && bonusNum > 0 ? { winnerBonusEcs: bonusNum } : {}),
  }
}

/** Global recent ranked ECS/USDT finishes (no wallet filter). */
export const getRecentRankedBattleFeed = async (opts?: {
  limit?: number
  beforeBattleEndedAt?: string
  beforeCursorId?: string
}): Promise<{
  success: boolean
  data?: { entries: PvpRankedBattleFeedEntry[] }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/ranked-battle-feed-recent', {
      limit: opts?.limit,
      beforeBattleEndedAt: opts?.beforeBattleEndedAt,
      beforeCursorId: opts?.beforeCursorId,
    })
    const raw = response.data?.data?.entries
    if (response.data?.success && Array.isArray(raw)) {
      const entries = raw
        .map((row: Record<string, unknown>) => parseRankedFeedEntry(row))
        .filter((e): e is PvpRankedBattleFeedEntry => e != null)
      return { success: true, data: { entries } }
    }
    return { success: !!response.data?.success, message: response.data?.message }
  } catch (error: any) {
    console.error('Error fetching ranked battle feed:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch ranked battle feed',
    }
  }
}

function parseActiveRankedCharacterThumb(
  row: Record<string, unknown>,
): PvpActiveRankedBattleCharacterThumb | null {
  const characterId = typeof row.characterId === 'string' ? row.characterId.trim() : ''
  const rawUrl = row.imageUrl
  const imageUrl =
    rawUrl != null && typeof rawUrl === 'string' && rawUrl.trim() ? rawUrl.trim() : null
  const rawR = row.rarity
  const rarityRaw =
    rawR != null && typeof rawR === 'string' && rawR.trim() ? rawR.trim().toLowerCase() : null
  const rarity =
    rarityRaw === 'rare' || rarityRaw === 'legendary' ? rarityRaw : rarityRaw ? 'common' : null
  if (!characterId && !imageUrl) return null
  return { characterId: characterId || '?', imageUrl, rarity }
}

function parseActiveRankedCharacterThumbList(raw: unknown): PvpActiveRankedBattleCharacterThumb[] {
  if (!Array.isArray(raw)) return []
  const out: PvpActiveRankedBattleCharacterThumb[] = []
  for (const item of raw) {
    if (item != null && typeof item === 'object') {
      const t = parseActiveRankedCharacterThumb(item as Record<string, unknown>)
      if (t) out.push(t)
    }
  }
  return out
}

function parseOptionalPositiveInt(raw: unknown): number | null {
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  const f = Math.floor(n)
  return f >= 1 ? f : null
}

function parseActiveRankedBattleRow(
  row: Record<string, unknown>,
): PvpActiveRankedBattleEntry | null {
  const battleId = typeof row.battleId === 'string' ? row.battleId.trim() : ''
  const roomId = typeof row.roomId === 'string' ? row.roomId.trim() : ''
  const player1 = typeof row.player1 === 'string' ? row.player1.trim() : ''
  const player2 = typeof row.player2 === 'string' ? row.player2.trim() : ''
  if (!battleId || !roomId || !player1 || !player2) return null
  const cur = row.currency
  const currency = cur === 'ecs' || cur === 'usdt' ? cur : null
  if (!currency) return null
  const startedAt =
    typeof row.startedAt === 'string' && row.startedAt.trim()
      ? row.startedAt.trim()
      : new Date().toISOString()
  const n1 = row.player1Nickname
  const n2 = row.player2Nickname
  const a1 = row.player1AvatarUrl
  const a2 = row.player2AvatarUrl
  return {
    battleId,
    roomId,
    player1,
    player2,
    player1Nickname: n1 != null && typeof n1 === 'string' && n1.trim() ? n1.trim() : null,
    player2Nickname: n2 != null && typeof n2 === 'string' && n2.trim() ? n2.trim() : null,
    player1Level: parseOptionalPositiveInt(row.player1Level),
    player2Level: parseOptionalPositiveInt(row.player2Level),
    player1AvatarUrl: a1 != null && typeof a1 === 'string' && a1.trim() ? a1.trim() : null,
    player2AvatarUrl: a2 != null && typeof a2 === 'string' && a2.trim() ? a2.trim() : null,
    player1Characters: parseActiveRankedCharacterThumbList(row.player1Characters),
    player2Characters: parseActiveRankedCharacterThumbList(row.player2Characters),
    currency,
    stakeAmount: (() => {
      const raw = row.stakeAmount ?? row.price
      const n = Number(raw)
      return Number.isFinite(n) && n >= 0 ? n : 0
    })(),
    startedAt,
  }
}

export function parseActiveRankedBattleFromSocket(
  data: unknown,
): PvpActiveRankedBattleEntry | null {
  if (data == null || typeof data !== 'object') return null
  return parseActiveRankedBattleRow(data as Record<string, unknown>)
}

/** In-progress ECS/USDT stake battles: ranked matchmaking (lobby queue) and ranked direct challenge. */
export const getActiveRankedLobbyBattles = async (opts?: {
  limit?: number
}): Promise<{
  success: boolean
  data?: { entries: PvpActiveRankedBattleEntry[] }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/active-ranked-battles', {
      limit: opts?.limit,
    })
    const raw = response.data?.data?.entries
    if (response.data?.success && Array.isArray(raw)) {
      const entries = raw
        .map((row: Record<string, unknown>) => parseActiveRankedBattleRow(row))
        .filter((e): e is PvpActiveRankedBattleEntry => e != null)
      return { success: true, data: { entries } }
    }
    return { success: !!response.data?.success, message: response.data?.message }
  } catch (error: any) {
    console.error('Error fetching active ranked battles:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch active ranked battles',
    }
  }
}

function parseTodayRankedChampion(row: unknown): PvpTodayRankedChampion | null {
  if (row == null || typeof row !== 'object') return null
  const o = row as Record<string, unknown>
  const wallet = typeof o.wallet === 'string' ? o.wallet.trim() : ''
  if (!wallet) return null
  const battleWins = Number(o.battleWins)
  const totalBonus = Number(o.totalBonus)
  if (!Number.isFinite(battleWins) || battleWins < 1) return null
  if (!Number.isFinite(totalBonus)) return null
  const rawNick = o.nickname ?? o.nickName
  const nick = rawNick != null && typeof rawNick === 'string' ? rawNick.trim() || null : null
  return {
    wallet,
    nickname: nick,
    battleWins: Math.floor(battleWins),
    totalBonus,
  }
}

/** Top ECS/USDT net bonus leaders over the rolling last 24 hours (server clock). */
export const getTodayRankedBattleChampions = async (): Promise<{
  success: boolean
  data?: { usdt: PvpTodayRankedChampion | null; ecs: PvpTodayRankedChampion | null }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/ranked-battle-champions-today', {})
    const d = response.data?.data
    if (!response.data?.success || d == null || typeof d !== 'object') {
      return { success: !!response.data?.success, message: response.data?.message }
    }
    const dd = d as Record<string, unknown>
    return {
      success: true,
      data: {
        usdt: parseTodayRankedChampion(dd.usdt),
        ecs: parseTodayRankedChampion(dd.ecs),
      },
    }
  } catch (error: any) {
    console.error('Error fetching last-24h ranked battle champions:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch ranked payout leaders',
    }
  }
}

function mapPvpMmrLeaderboardRow(row: Record<string, unknown>): PvpMmrLeaderboardEntry {
  const base: PvpMmrLeaderboardEntry = {
    rank: Number(row.rank) || 0,
    walletAddress: String(row.walletAddress ?? ''),
    nickName: (() => {
      const v = row.nickName
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
    userLvl: (() => {
      const n = Number(row.userLvl)
      return Number.isFinite(n) && n > 0 ? n : 1
    })(),
    userExp: (() => {
      const n = Number(row.userExp)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpMmr: (() => {
      const n = Number(row.pvpMmr)
      return Number.isFinite(n) ? n : 0
    })(),
    pvpWins: (() => {
      const n = Number(row.pvpWins)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpLosses: (() => {
      const n = Number(row.pvpLosses)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    pvpTies: (() => {
      const n = Number(row.pvpTies)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    })(),
    avatarUrl: (() => {
      const v = row.avatarUrl
      if (v == null || typeof v !== 'string') return null
      const s = v.trim()
      return s ? s : null
    })(),
  }
  return base
}

export const getPvPMmrLeaderboard = async (
  walletAddress: string,
  opts?: { limit?: number; skip?: number },
): Promise<{
  success: boolean
  data?: {
    entries: PvpMmrLeaderboardListRow[]
    total: number
    limit: number
    skip: number
    /** Current viewer’s row (global rank); always returned when the user exists. */
    viewerEntry?: PvpMmrLeaderboardEntry | null
    /** Server returns a fixed top-50 (+ ellipsis + pinned row) snapshot; no pagination. */
    snapshotMode?: boolean
  }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/mmr-leaderboard', {
      walletAddress,
      limit: opts?.limit,
      skip: opts?.skip,
    })
    const d = response.data?.data
    if (response.data?.success && Array.isArray(d?.entries)) {
      const entries: PvpMmrLeaderboardListRow[] = d.entries.map((row: Record<string, unknown>) =>
        parsePvpMmrLeaderboardListRow(row),
      )
      const mapViewer = (
        row: Record<string, unknown> | null | undefined,
      ): PvpMmrLeaderboardEntry | null => {
        if (!row || typeof row !== 'object') return null
        const wa = String(row.walletAddress ?? '')
        if (!wa) return null
        return parsePvpMmrLeaderboardEntry(row as Record<string, unknown>)
      }

      return {
        success: true,
        data: {
          entries,
          total: Number(d.total ?? 0),
          limit: Number(d.limit ?? 50),
          skip: Number(d.skip ?? 0),
          viewerEntry: mapViewer(d.viewerEntry as Record<string, unknown>),
          snapshotMode: Boolean(d.snapshotMode),
        },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error fetching PvP MMR leaderboard:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch leaderboard',
    }
  }
}

export const searchPvpPlayersByQuery = async (
  walletAddress: string,
  query: string,
  opts?: { skip?: number; limit?: number },
): Promise<{
  success: boolean
  data?: {
    entries: PvpMmrLeaderboardEntry[]
    trainingBotEntries?: PvpMmrLeaderboardEntry[]
    skip?: number
    limit?: number
    hasMore?: boolean
    totalCount?: number
  }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/search-players', {
      walletAddress,
      query: query.trim(),
      skip: opts?.skip ?? 0,
      limit: opts?.limit ?? 50,
    })
    const d = response.data?.data
    if (response.data?.success && Array.isArray(d?.entries)) {
      const trainingRaw = d.trainingBotEntries
      const trainingBotEntries = Array.isArray(trainingRaw)
        ? trainingRaw.map((row: Record<string, unknown>) => parsePvpMmrLeaderboardEntry(row))
        : undefined
      return {
        success: true,
        data: {
          entries: d.entries.map((row: Record<string, unknown>) =>
            parsePvpMmrLeaderboardEntry(row),
          ),
          ...(trainingBotEntries?.length ? { trainingBotEntries } : {}),
          skip: typeof d.skip === 'number' ? d.skip : undefined,
          limit: typeof d.limit === 'number' ? d.limit : undefined,
          hasMore: typeof d.hasMore === 'boolean' ? d.hasMore : undefined,
          totalCount: typeof d.totalCount === 'number' ? d.totalCount : undefined,
        },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error searching PvP players:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Search failed',
    }
  }
}

export type PvpHonorRewardKind = 'oneTime' | 'daily' | 'weekly'

export type PvpHonorCatalogTier = {
  tier: string
  minMmr: number
  oneTime: { lines: string[] }
  daily: { lines: string[] }
  weekly: { lines: string[] }
  permanentEffect: { lines: string[] }
}

export type PvpHonorClaimsRow = {
  oneTimeClaimed: boolean
  dailyClaimed: boolean
  weeklyClaimed: boolean
}

export const getPvpHonorRewardsCatalog = async (): Promise<{
  success: boolean
  data?: { resourcePackLegend: unknown; tiers: PvpHonorCatalogTier[] }
  message?: string
}> => {
  try {
    const response = await axios.get('/pvp/honor-rewards/catalog')
    const d = response.data?.data
    if (response.data?.success && d?.tiers && Array.isArray(d.tiers)) {
      return { success: true, data: d }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error fetching PvP honor rewards catalog:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch honor rewards',
    }
  }
}

export const postPvpHonorRewardsState = async (
  walletAddress: string,
): Promise<{
  success: boolean
  data?: { claimsByTier: Record<string, PvpHonorClaimsRow> }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/honor-rewards/state', { walletAddress })
    const d = response.data?.data
    if (response.data?.success && d?.claimsByTier && typeof d.claimsByTier === 'object') {
      return {
        success: true,
        data: { claimsByTier: d.claimsByTier as Record<string, PvpHonorClaimsRow> },
      }
    }
    return {
      success: !!response.data?.success,
      message: response.data?.message,
    }
  } catch (error: any) {
    console.error('Error fetching PvP honor rewards state:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch claim state',
    }
  }
}

export const getPvPRoomDetails = async (
  walletAddress: string,
  roomId: string,
): Promise<{
  success: boolean
  room?: PvPRoom
  message?: string
}> => {
  const response = await axios.get(`/pvp/rooms/${encodeURIComponent(roomId)}`, {
    params: { walletAddress },
  })
  return {
    success: response.data?.success || false,
    room: response.data?.data,
    message: response.data?.message,
  }
}

/** Active `battle_active` document — includes full `player*.characters` (id, rarity, portraits). */
export const getPvpBattleState = async (
  walletAddress: string,
  battleId: string,
  opts?: { asSpectator?: boolean },
): Promise<{ success: boolean; battle?: Record<string, unknown>; message?: string }> => {
  try {
    const response = await axios.post('/pvp/get-battle', {
      walletAddress,
      battleId,
      ...(opts?.asSpectator ? { asSpectator: true } : {}),
    })
    const b = response.data?.data?.battle
    return {
      success: response.data?.success !== false && !!b,
      battle: b,
      message: response.data?.message,
    }
  } catch {
    return { success: false }
  }
}

export const purchaseAvatar = async (
  walletAddress: string,
  avatarId: string,
): Promise<{
  success: boolean
  user?: any
  purchasedAvatar?: string
  remainingECS?: number
  message?: string
}> => {
  const response = await axios.post('/user/avatars/purchase', {
    walletAddress,
    avatarId,
  })
  return response.data
}

export const surrenderPvPBattle = async (
  walletAddress: string,
  battleId: string,
  roomId?: string,
): Promise<{
  success: boolean
  battle: any
  winner: string
  reason: string
  winnerReward?: any
  loserReward?: any
}> => {
  const response = await axios.post('/pvp/surrender', {
    walletAddress,
    battleId,
    roomId,
  })
  return response.data
}

export const postPvpDirectChallengeRequest = async (body: {
  walletAddress: string
  opponentWalletAddress: string
  battleMode: 'ranked' | 'unranked'
  stakeCurrency?: 'usdt' | 'ecs'
  stakeAmount?: number
  useCustomStake?: boolean
  characterCount: number
  requesterCharacterInstanceIds: string[]
  /**
   * Tournament challenge: the server additionally requires both wallets to be in the same
   * tournament group and to have not battled each other yet.
   */
  isTournament?: boolean
}): Promise<{
  success: boolean
  data?: { challengeId: string; pendingExpiresAtUnixMs?: number; challengeCreatedAtUnixMs?: number }
  message?: string
  /** Tournament rejection reason, e.g. `TOURNAMENT_ALREADY_PLAYED`, `TOURNAMENT_DIFFERENT_GROUP`. */
  code?: string
}> => {
  try {
    const response = await axios.post('/pvp/direct-challenge/request', body)
    const d = response.data
    /** Backend `RESPONSE()` merges payload at top level (`challengeId`), not under `data`. */
    const rawId = d?.challengeId ?? d?.data?.challengeId
    const challengeId =
      rawId != null && String(rawId).trim() !== '' ? String(rawId).trim() : undefined
    const rawExp = d?.pendingExpiresAtUnixMs
    const pendingExpiresAtUnixMs =
      typeof rawExp === 'number' && Number.isFinite(rawExp) ? rawExp : undefined
    const rawCreated = d?.challengeCreatedAtUnixMs
    const challengeCreatedAtUnixMs =
      typeof rawCreated === 'number' && Number.isFinite(rawCreated) ? rawCreated : undefined
    return {
      success: !!d?.success,
      ...(challengeId
        ? {
            data: {
              challengeId,
              ...(pendingExpiresAtUnixMs != null ? { pendingExpiresAtUnixMs } : {}),
              ...(challengeCreatedAtUnixMs != null ? { challengeCreatedAtUnixMs } : {}),
            },
          }
        : {}),
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    const code = error?.response?.data?.code
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to send direct challenge',
      ...(typeof code === 'string' && code ? { code } : {}),
    }
  }
}

export type PvpDirectChallengeAcceptOkData = {
  challengeId: string
  requesterWallet: string
  opponentWallet: string
  opponentNickname: string
  opponentAvatarUrl?: string | null
  opponentUserLvl?: number
  opponentPvpMmr?: number
  serverNowMs: number
  joinPrepExpiresAtUnixMs: number
  challengeCreatedAtUnixMs?: number
  battleMode: 'ranked' | 'unranked'
  stakeCurrency?: 'usdt' | 'ecs'
  stakeAmount: number
  characterCount: number
}

export const postPvpDirectChallengeAccept = async (body: {
  walletAddress: string
  challengeId: string
}): Promise<{ success: boolean; data?: PvpDirectChallengeAcceptOkData; message?: string }> => {
  try {
    const response = await axios.post('/pvp/direct-challenge/accept', body)
    const d = response.data
    const rawId = d?.challengeId ?? d?.data?.challengeId
    const challengeId =
      rawId != null && String(rawId).trim() !== '' ? String(rawId).trim() : undefined
    if (!d?.success || !challengeId) {
      return {
        success: false,
        message: typeof d?.message === 'string' ? d.message : undefined,
      }
    }
    const joinPrepRaw = d.joinPrepExpiresAtUnixMs
    const joinPrepExpiresAtUnixMs =
      typeof joinPrepRaw === 'number'
        ? joinPrepRaw
        : typeof joinPrepRaw === 'string'
          ? Number(joinPrepRaw)
          : NaN
    const serverNowRaw = d.serverNowMs
    const serverNowMs =
      typeof serverNowRaw === 'number'
        ? serverNowRaw
        : typeof serverNowRaw === 'string'
          ? Number(serverNowRaw)
          : Date.now()
    const ccRaw = Number(d.characterCount)
    const characterCount =
      Number.isFinite(ccRaw) && ccRaw >= 1 && ccRaw <= 3 ? Math.floor(ccRaw) : 1
    const stakeAmount = Number(d.stakeAmount)
    const battleMode = d.battleMode === 'ranked' ? 'ranked' : 'unranked'
    const stakeCurrency =
      d.stakeCurrency === 'ecs' ? 'ecs' : d.stakeCurrency === 'usdt' ? 'usdt' : undefined
    const createdRaw = d.challengeCreatedAtUnixMs
    const challengeCreatedAtUnixMs =
      createdRaw != null && Number.isFinite(Number(createdRaw))
        ? Math.floor(Number(createdRaw))
        : undefined

    const data: PvpDirectChallengeAcceptOkData = {
      challengeId,
      requesterWallet: String(d.requesterWallet ?? '').trim(),
      opponentWallet: String(d.opponentWallet ?? '').trim(),
      opponentNickname: String(d.opponentNickname ?? '').trim(),
      opponentAvatarUrl: d.opponentAvatarUrl ?? null,
      opponentUserLvl:
        d.opponentUserLvl != null && Number.isFinite(Number(d.opponentUserLvl))
          ? Math.max(1, Math.floor(Number(d.opponentUserLvl)))
          : undefined,
      opponentPvpMmr:
        d.opponentPvpMmr != null && Number.isFinite(Number(d.opponentPvpMmr))
          ? Math.floor(Number(d.opponentPvpMmr))
          : undefined,
      serverNowMs: Number.isFinite(serverNowMs) ? Math.floor(serverNowMs) : Date.now(),
      joinPrepExpiresAtUnixMs: Number.isFinite(joinPrepExpiresAtUnixMs)
        ? Math.floor(joinPrepExpiresAtUnixMs)
        : 0,
      ...(challengeCreatedAtUnixMs != null ? { challengeCreatedAtUnixMs } : {}),
      battleMode,
      stakeCurrency,
      stakeAmount: battleMode === 'ranked' && Number.isFinite(stakeAmount) ? stakeAmount : 0,
      characterCount,
    }
    return {
      success: true,
      data,
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to accept challenge',
    }
  }
}

export const postPvpDirectChallengeDecline = async (body: {
  walletAddress: string
  challengeId: string
}): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/direct-challenge/decline', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to decline challenge',
    }
  }
}

export const postPvpDirectChallengeCancel = async (body: {
  walletAddress: string
  challengeId: string
}): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/direct-challenge/cancel', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to cancel challenge',
    }
  }
}

export const postPvpDirectChallengeJoinBattle = async (body: {
  walletAddress: string
  challengeId: string
  joinerCharacterInstanceIds: string[]
}): Promise<{
  success: boolean
  data?: {
    challengeId: string
    roomId?: string
    battleMode: string
    countdownSeconds?: number
    /** Authoritative server ms when join-battle was processed (matches socket countdown). */
    serverNowMs?: number
    /** Wall-clock ms when the battle-start countdown ends on the server. */
    countdownEndsAtUnixMs?: number
    needsPayment: boolean
  }
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/direct-challenge/join-battle', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to join battle',
    }
  }
}

export const postPvpFriendRequest = async (body: {
  walletAddress: string
  targetWalletAddress: string
}): Promise<{
  success: boolean
  requestId?: string
  relation?: PvpFriendRelationStatus
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/request', body)
    const d = response.data
    return {
      success: !!d?.success,
      requestId: d?.requestId != null ? String(d.requestId) : undefined,
      relation: d?.relation,
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to send friend request',
    }
  }
}

export const postPvpFriendAccept = async (body: {
  walletAddress: string
  requestId: string
}): Promise<{
  success: boolean
  friend?: PvpMmrLeaderboardEntry
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/accept', body)
    const d = response.data
    return {
      success: !!d?.success,
      friend: d?.friend,
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to accept friend request',
    }
  }
}

export const postPvpFriendDecline = async (body: {
  walletAddress: string
  requestId: string
}): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/friends/decline', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to decline friend request',
    }
  }
}

export const postPvpFriendCancel = async (body: {
  walletAddress: string
  requestId?: string
  targetWalletAddress?: string
}): Promise<{ success: boolean; relation?: PvpFriendRelationStatus; message?: string }> => {
  try {
    const response = await axios.post('/pvp/friends/cancel', body)
    const d = response.data
    return {
      success: !!d?.success,
      relation: d?.relation,
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to cancel friend request',
    }
  }
}

export const postPvpFriendRemove = async (body: {
  walletAddress: string
  targetWalletAddress: string
}): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/friends/remove', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to remove friend',
    }
  }
}

/** Resource gift between two friends — independent of alliance membership (unlike
 *  allianceApi.ts's `donateToMember`, which requires same-alliance + a tech unlock).
 *  Capped per UTC day by the GEM VALUE of the gift on both sides — what the sender may give
 *  away and what the recipient may accept (see `postPvpFriendDonationQuota`). */
export const postPvpFriendDonate = async (body: {
  walletAddress: string
  targetWalletAddress: string
  resources: Record<string, number>
}): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/friends/donate', body)
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to send donation',
    }
  }
}

/** Today's remaining friend-donation budgets, in gems: the caller's giving budget and — when
 *  `targetWalletAddress` is given — that friend's receiving budget. `resourceGemValue` is the
 *  server's per-unit price table, so the client totals a bundle with the same numbers the
 *  server will check it against instead of hard-coding its own copy. */
export const postPvpFriendDonationQuota = async (body: {
  walletAddress: string
  targetWalletAddress?: string
}): Promise<{
  success: boolean
  message?: string
  sendCap?: number
  sendRemaining?: number
  receiveCap?: number
  receiveRemaining?: number | null
  resourceGemValue?: Record<string, number>
}> => {
  try {
    const response = await axios.post('/pvp/friends/donation-quota', body)
    const d = response.data
    return {
      success: !!d?.success,
      sendCap: Number(d?.sendCap) || 0,
      sendRemaining: Number(d?.sendRemaining) || 0,
      receiveCap: Number(d?.receiveCap) || 0,
      receiveRemaining: typeof d?.receiveRemaining === 'number' ? d.receiveRemaining : null,
      resourceGemValue: d?.resourceGemValue || {},
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to load donation limit',
    }
  }
}

export const postPvpFriendList = async (body: {
  walletAddress: string
}): Promise<{
  success: boolean
  friends?: PvpMmrLeaderboardEntry[]
  totalCount?: number
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/list', body)
    const d = response.data
    const friends = Array.isArray(d?.friends) ? d.friends : []
    return {
      success: !!d?.success,
      friends,
      totalCount:
        typeof d?.totalCount === 'number' && Number.isFinite(d.totalCount)
          ? Math.max(0, Math.floor(d.totalCount))
          : friends.length,
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      friends: [],
      message: error?.response?.data?.message || 'Failed to load friends',
    }
  }
}

export const postPvpFriendRelations = async (body: {
  walletAddress: string
  walletAddresses: string[]
}): Promise<{
  success: boolean
  relations?: Record<string, PvpFriendRelationStatus>
  requestIds?: Record<string, string>
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/relations', body)
    const d = response.data
    return {
      success: !!d?.success,
      relations: d?.relations && typeof d.relations === 'object' ? d.relations : {},
      requestIds: d?.requestIds && typeof d.requestIds === 'object' ? d.requestIds : {},
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      relations: {},
      requestIds: {},
      message: error?.response?.data?.message || 'Failed to load friend relations',
    }
  }
}

export const postPvpFriendPendingIncoming = async (body: {
  walletAddress: string
}): Promise<{
  success: boolean
  requests?: Array<{
    requestId: string
    requesterWallet: string
    requesterNickname: string
    requesterAvatarUrl?: string
    requesterUserLvl?: number
    requesterPvpMmr?: number
    createdAtUnixMs?: number
  }>
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/pending-incoming', body)
    const d = response.data
    return {
      success: !!d?.success,
      requests: Array.isArray(d?.requests) ? d.requests : [],
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      requests: [],
      message: error?.response?.data?.message || 'Failed to load pending requests',
    }
  }
}

/** Requests I sent that are still awaiting the other side's response. */
export const postPvpFriendPendingOutgoing = async (body: {
  walletAddress: string
}): Promise<{
  success: boolean
  requests?: Array<{
    requestId: string
    recipientWallet: string
    recipientNickname: string
    recipientAvatarUrl?: string
    recipientUserLvl?: number
    recipientPvpMmr?: number
    createdAtUnixMs?: number
  }>
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/friends/pending-outgoing', body)
    const d = response.data
    return {
      success: !!d?.success,
      requests: Array.isArray(d?.requests) ? d.requests : [],
      message: typeof d?.message === 'string' ? d.message : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      requests: [],
      message: error?.response?.data?.message || 'Failed to load sent requests',
    }
  }
}

export const postPvpProcessPayment = async (
  walletAddress: string,
  roomId: string,
): Promise<{ success: boolean; room?: unknown; bothPaid?: boolean; message?: string }> => {
  try {
    const response = await axios.post('/pvp/process_payment', { walletAddress, roomId })
    return response.data
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Payment failed',
    }
  }
}

export interface BattlePreviewData {
  roomId: string
  battleId: string
  turnNumber: number
  status: string
  player1: {
    walletAddress: string
    nickname: string
    level: number
    avatar: string | null
    activeCharacter: {
      characterId: string
      characterName: string
      currentHp: number
      maxHp: number
      currentSp: number
      maxSp: number
    } | null
    totalCharacters: number
    aliveCharacters: number
  }
  player2: {
    walletAddress: string
    nickname: string
    level: number
    avatar: string | null
    activeCharacter: {
      characterId: string
      characterName: string
      currentHp: number
      maxHp: number
      currentSp: number
      maxSp: number
    } | null
    totalCharacters: number
    aliveCharacters: number
  }
}

export const getBattlePreview = async (
  roomId: string,
): Promise<{
  success: boolean
  data?: BattlePreviewData
  message?: string
}> => {
  try {
    const response = await axios.post('/pvp/get-battle-preview', { roomId })
    return response.data
  } catch (error: any) {
    console.error('Error fetching battle preview:', error)
    return {
      success: false,
      message: error?.response?.data?.message || 'Failed to fetch battle preview',
    }
  }
}

export const changeAvatar = async (
  walletAddress: string,
  avatarId: string | null,
): Promise<{
  success: boolean
  user?: any
  currentAvatar?: string | null | { id: string | null; imageUrl: string | null }
  message?: string
}> => {
  const response = await axios.post('/user/avatars/change', {
    walletAddress,
    avatarId,
  })
  return response.data
}

// Espionage API functions
export const trainEspionageXp = async (
  walletAddress: string,
  characterId: string,
  trainingBuildingLevel: number,
) => {
  const response = await axios.post('/land/espionage/train-xp', {
    walletAddress,
    characterId,
    trainingBuildingLevel,
  })
  return response.data
}

export const claimEspionageXpTraining = async (walletAddress: string, characterId: string) => {
  const response = await axios.post('/land/espionage/claim-xp-training', {
    walletAddress,
    characterId,
  })
  return response.data
}

export const immediateEspionageXpTraining = async (walletAddress: string, characterId: string) => {
  const response = await axios.post('/land/espionage/immediate-xp-training', {
    walletAddress,
    characterId,
  })
  return response.data
}

export const claimSpySkillLearning = async (walletAddress: string, characterId: string) => {
  const response = await axios.post('/land/espionage/claim-skill-learning', {
    walletAddress,
    characterId,
  })
  return response.data
}

export const cancelEspionageXpTraining = async (walletAddress: string, characterId: string) => {
  const response = await axios.post('/land/espionage/cancel-xp-training', {
    walletAddress,
    characterId,
  })
  return response.data
}

export const cancelSpySkillLearning = async (walletAddress: string, characterId: string) => {
  const response = await axios.post('/land/espionage/cancel-skill-learning', {
    walletAddress,
    characterId,
  })
  return response.data
}

export const learnSpySkill = async (
  walletAddress: string,
  characterId: string,
  skillLevel: number,
) => {
  const response = await axios.post('/land/espionage/learn-skill', {
    walletAddress,
    characterId,
    skillLevel,
  })
  return response.data
}

export const trainEspionage = async (
  walletAddress: string,
  characterId: string,
  castleId?: number,
) => {
  const response = await axios.post('/land/espionage/train', {
    walletAddress,
    characterId,
    castleId: castleId || 0,
  })
  return response.data
}

export const trainCounterEspionage = async (walletAddress: string, castleId?: number) => {
  const response = await axios.post('/land/espionage/train-counter', {
    walletAddress,
    castleId: castleId || 0,
  })
  return response.data
}

export const assignCounterEspionageCharacter = async (
  walletAddress: string,
  characterId: string | null,
) => {
  const response = await axios.post('/land/espionage/assign-counter-spy', {
    walletAddress,
    characterId: characterId || null,
  })
  return response.data
}

export const getCounterEspionageStatus = async (walletAddress: string) => {
  const response = await axios.post('/land/espionage/counter-status', { walletAddress })
  return response.data
}

export const startEspionageMission = async (
  walletAddress: string,
  characterId: string,
  targetWalletAddress: string,
  missionLevel: number,
  targetCharacterId?: string,
) => {
  const response = await axios.post('/land/espionage/start-mission', {
    walletAddress,
    characterId,
    targetWalletAddress,
    missionLevel,
    targetCharacterId: targetCharacterId || null,
  })
  return response.data
}

export const recallEspionageMission = async (walletAddress: string, missionId: string) => {
  const response = await axios.post('/land/espionage/recall-mission', {
    walletAddress,
    missionId,
  })
  return response.data
}

export const getActiveEspionageMissions = async (walletAddress: string) => {
  const response = await axios.post('/land/espionage/active-missions', {
    walletAddress,
  })
  return response.data
}

export const getCounterEspionageAlerts = async (walletAddress: string) => {
  const response = await axios.post('/land/espionage/alerts', {
    walletAddress,
  })
  return response.data
}

export const attemptCaptureSpy = async (walletAddress: string, alertId: string) => {
  const response = await axios.post('/land/espionage/capture', {
    walletAddress,
    alertId,
  })
  return response.data
}

export const requestRansom = async (
  walletAddress: string,
  alertId: string,
  ransomAmount: number,
) => {
  const response = await axios.post('/land/espionage/request-ransom', {
    walletAddress,
    alertId,
    ransomAmount,
  })
  return response.data
}

export const acceptRansom = async (walletAddress: string, alertId: string) => {
  const response = await axios.post('/land/espionage/accept-ransom', {
    walletAddress,
    alertId,
  })
  return response.data
}

export const getRansomRequests = async (walletAddress: string) => {
  const response = await axios.post('/land/espionage/ransom-requests', {
    walletAddress,
  })
  return response.data
}

export const beheadSpy = async (walletAddress: string, alertId: string) => {
  const response = await axios.post('/land/espionage/behead', {
    walletAddress,
    alertId,
  })
  return response.data
}

export const getTargetCastleInfo = async (
  walletAddress: string,
  targetWalletAddress: string,
  options?: { live?: boolean },
) => {
  const response = await axios.post('/land/espionage/target-info', {
    walletAddress,
    targetWalletAddress,
    ...(options?.live ? { live: true } : {}),
  })
  return response.data
}

// Technology Research APIs
export const getTechnologyStatus = async (walletAddress: string) => {
  const response = await axios.post('/land/technology/status', { walletAddress })
  return response.data
}

/** Complete Survival kingdom research slot (e.g. slot 0 = Item usage in battle). */
export const completeKingdomSurvivalResearchSlot = async (
  walletAddress: string,
  slotIndex: number,
) => {
  const response = await axios.post('/land/technology/kingdom-survival-complete', {
    walletAddress,
    slotIndex,
  })
  return response.data
}

/** Start timed kingdom technology tree research (30 min). */
export const startKingdomTreeResearch = async (
  walletAddress: string,
  era: string,
  slotIndex: number,
) => {
  const response = await axios.post('/land/technology/kingdom-tree-research-start', {
    walletAddress,
    era,
    slotIndex,
  })
  return response.data
}

// ==================== MvM Alliance Battle ====================
// In-battle reads/actions use Socket.IO (mvm:get-battle, mvm:join-battle, mvm:execute-turn, etc.)

export {
  startMvMAttack,
  mvmAttackArrived,
  mvmDefenderSelect,
  mvmDefenderSelectAndConfirm,
  mvmDefenderTowerMarch,
  mvmConfirmBattle,
  mvmAutoDefend,
  joinMvMRally,
  joinMvMReinforcement,
  leaveMvMRallyJoin,
  leaveMvMReinforcement,
  requestDefenseHelp,
} from './mvmPendingSocket'

export interface MvmCombatPowerCharacter {
  characterInstanceId: string
  characterId: string
  characterName: string
  rarity: string
  level: number
  combatPower: number
  inTopRoster: boolean
}

export interface MvmCombatPowerSummary {
  combatPower: number
  heroCap: number
  characterCount: number
  characters: MvmCombatPowerCharacter[]
  antiFarm?: {
    maxPowerRatio: number
    minAttackerCombatPower: number
    retaliationWindowMs: number
  }
}

/** Own total combat power + per-character breakdown (server-authoritative formula). */
export const getMyMvmCombatPower = async (
  walletAddress: string,
): Promise<MvmCombatPowerSummary> => {
  const response = await axios.post('/mvm/combat-power', { walletAddress })
  return response.data
}

export interface MvmPreBattleHealEntry {
  characterId: string
  currentHp: number
  maxHp: number
  missingHp: number
  cost: number
}

/**
 * Bread cost to top the given characters up to full HP before committing them to an MvM battle
 * (castle attack, rally join, or reinforcement) — quote only, no charge. Reuses the hospital's
 * bread-per-HP formula server-side, alliance discount included.
 */
export const previewMvmPreBattleHeal = async (
  walletAddress: string,
  characterIds: string[],
): Promise<{ entries: MvmPreBattleHealEntry[]; totalCost: number; userBread: number }> => {
  const response = await axios.post('/mvm/battle/heal-preview', { walletAddress, characterIds })
  return response.data
}

/** Charges the (server-recomputed) bread cost and heals the given characters to full HP in one call. */
export const confirmMvmPreBattleHeal = async (
  walletAddress: string,
  characterIds: string[],
): Promise<{
  bread: number
  healed: Array<{ characterId: string; currentHp: number; maxHp: number }>
}> => {
  const response = await axios.post('/mvm/battle/heal-confirm', { walletAddress, characterIds })
  return response.data
}

export const getMvMPendingAttacks = async (walletAddress: string) => {
  const response = await axios.get('/mvm/pending-attacks', { params: { walletAddress } })
  return response.data
}

export interface MvmReinforcementEligibility {
  attackId: string
  reinforceable: boolean
  /** 'ok' | 'not_found' | 'phase_closed' | 'is_castle_owner' | 'not_in_alliance' | 'already_joined' | 'cap_reached' */
  reason: string
  message: string
  pendingStatus: string | null
  attackType: string | null
  attackerWallet: string | null
  defenderWallet: string | null
  worldId: string | null
  arrivesAt: string | null
}

/**
 * Can this wallet still reinforce this siege? Backs the reinforcement mail's "Help defend" button.
 *
 * Deliberately NOT `getMvMPendingAttacks`: that endpoint only returns sieges the viewer already takes
 * part in, so an ally who has not joined yet never finds the attack there — which is what made the
 * button report "the battle has already ended" while the attacker was still marching.
 */
export const getMvMReinforcementEligibility = async (
  walletAddress: string,
  attackId: string,
): Promise<MvmReinforcementEligibility> => {
  const response = await axios.get('/mvm/reinforce/eligibility', {
    params: { walletAddress, attackId },
  })
  return response.data
}

export interface MvmRallyJoinEligibility {
  attackId: string
  joinable: boolean
  /** 'ok' | 'not_found' | 'phase_closed' | 'window_closed' | 'is_leader' | 'is_defender' | 'not_in_alliance' | 'already_joined' | 'cap_reached' */
  reason: string
  message: string
  pendingStatus: string | null
  leaderWallet: string | null
  leaderName: string | null
  targetWallet: string | null
  targetName: string | null
  worldId: string | null
  arrivesAt: string | null
  rallyPhaseEndAt: string | null
  marchTravelTimeMs: number | null
  leaderCharacterCount: number | null
  rallyJoinMinBread: number | null
}

/**
 * Can this wallet still join this rally? Backs the rally mail's "Join attack" button.
 * (Mirror of `getMvMReinforcementEligibility`.)
 */
export const getMvMRallyJoinEligibility = async (
  walletAddress: string,
  attackId: string,
): Promise<MvmRallyJoinEligibility> => {
  const response = await axios.get('/mvm/rally/eligibility', {
    params: { walletAddress, attackId },
  })
  return response.data
}

export const cancelMvMAttackByAttacker = async (walletAddress: string, attackId: string) => {
  return restCancelMvMAttackByAttacker(walletAddress, attackId)
}

export const getMvMPendingRosters = async (walletAddress: string) => {
  const response = await axios.get('/mvm/pending-rosters', { params: { walletAddress } })
  return response.data
}

export const fetchMvMCaptives = async (walletAddress: string) => {
  const response = await axios.post('/mvm/captives', { walletAddress })
  return response.data
}

export const recruitMvMCaptiveRequest = async (
  walletAddress: string,
  characterInstanceId: string,
) => {
  const response = await axios.post('/mvm/recruit-captive', { walletAddress, characterInstanceId })
  return response.data
}

export const beheadMvMCaptiveRequest = async (
  walletAddress: string,
  characterInstanceId: string,
) => {
  const response = await axios.post('/mvm/behead-captive', { walletAddress, characterInstanceId })
  return response.data
}

export const releaseMvMCaptiveRequest = async (
  walletAddress: string,
  characterInstanceId: string,
) => {
  const response = await axios.post('/mvm/release-captive', { walletAddress, characterInstanceId })
  return response.data
}

export type RansomResources = {
  water?: number
  wheat?: number
  bread?: number
  wood?: number
  stone?: number
  iron?: number
}

export const sendRansomRequestApi = async (
  walletAddress: string,
  characterInstanceId: string,
  resources: RansomResources,
) => {
  const response = await axios.post('/mvm/ransom-request', {
    walletAddress,
    characterInstanceId,
    resources,
  })
  return response.data
}

export const respondToRansomRequestApi = async (
  walletAddress: string,
  ransomId: string,
  accept: boolean,
) => {
  const response = await axios.post('/mvm/ransom-respond', { walletAddress, ransomId, accept })
  return response.data
}

export const getPendingRansomRequestsApi = async (walletAddress: string) => {
  const response = await axios.post('/mvm/pending-ransoms', { walletAddress })
  return response.data
}

export const fetchOnetConnectPersist = async (walletAddress: string): Promise<unknown | null> => {
  const { data } = await axios.post('/user/onet-connect/state', { walletAddress })
  return data?.persist ?? null
}

export type OnetGemMilestoneGrant = {
  milestoneId?: string
  message?: string
  kind?: string
  weaponName?: string
  characterId?: string
  rarity?: string
}

export const syncOnetConnectToServer = async (args: {
  walletAddress: string
  persist: unknown
  creditToMain?: Record<string, number>
}): Promise<{
  gemMilestoneGrants?: OnetGemMilestoneGrant[]
  onetConnectPersist?: unknown
}> => {
  const { data } = await axios.post('/user/onet-connect/sync', {
    walletAddress: args.walletAddress,
    persist: args.persist,
    creditToMain: args.creditToMain,
  })
  return data ?? {}
}

export type OnetRewardGrant = { label: string; bundle: Record<string, unknown>; line?: string }

/** Live tool/attempt counts from the browser (reward-events used to merge only DB clone — must match in-memory spend). */
export type OnetRewardEventsEconomy = {
  hintCharges?: number
  shuffleCharges?: number
  nextHintChargeAt?: number | null
  nextShuffleChargeAt?: number | null
  attempts?: number
  lastUtcHourBucket?: number | null
}

export const postOnetConnectRewardEvents = async (
  walletAddress: string,
  events: unknown[],
  liveEconomy?: OnetRewardEventsEconomy | null,
): Promise<{
  grants?: OnetRewardGrant[]
  persist?: unknown
  displayLines?: string[]
  gemMilestoneGrants?: OnetGemMilestoneGrant[]
}> => {
  const { data } = await axios.post('/user/onet-connect/reward-events', {
    walletAddress,
    events,
    ...(liveEconomy != null && typeof liveEconomy === 'object' ? { economy: liveEconomy } : {}),
  })
  return data ?? {}
}

// --- Energy refill purchase (diplomacy / espionage "+" button) ---
export interface EnergyPurchaseState {
  energy: number
  maxEnergy: number
  usdt: number
  countToday: number
  energyAmount: number
  usdtCost: number
}

export interface EnergyPurchaseResult {
  energy: number
  energyUpdatedAt: string
  maxEnergy: number
  usdt: number
  spent: number
  currency: 'ecs'
  energyAdded: number
  next: { usdtCost: number; countToday: number }
}

export const getEnergyPurchaseState = async (
  walletAddress: string,
): Promise<EnergyPurchaseState> => {
  const { data } = await axios.post('/user/energy/purchase-state', { walletAddress })
  return data as EnergyPurchaseState
}

export const purchaseEnergy = async (walletAddress: string): Promise<EnergyPurchaseResult> => {
  const { data } = await axios.post('/user/energy/purchase', { walletAddress })
  return data as EnergyPurchaseResult
}
